<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { validateEmail } from '@features/auth/utils/validation';
import SyncFieldIndicator from '@features/Entity_my/components/SyncFieldIndicator.vue';
import type {
    Contact,
    ContactInfo,
    SpcAddressConsolidationGroupTab,
    ContactAddressCandidate,
    ContactAddressTypeCode,
} from '../types/contact.types';
import type { AddressInfo } from '../types/contact.types';
import ContactAddressSelectDialog from './ContactAddressSelectDialog.vue';
import ContactBranchAddressShowDialog from './ContactBranchAddressShowDialog.vue';
import { mapCandidateToMyAddressData } from '../utils/contactAddressSelect';
import {
    applyBranchAddressRowToDraft,
    buildAllCorporateBranchShowItems,
    type BranchAddressShowItem,
} from '../utils/contactBranchAddressShow';
import { contactsApi } from '../api/contactsApi';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { joinAddressDisplayParts } from '../utils/addressJoinParts';
import { extractAddressArrayFromProfileJsonColumn } from '@features/Contact/utils/profileServiceBusinessColumn';
import {
    applyCountryChangeClearsSubLocation,
    normalizeMyLocationFieldForPersist,
    normalizeMyLocationRowForForm,
    resolveTotalCodeFromField,
    resolveCountryTotalCode,
    shouldShowMyLocationFields,
    countryCodeToSearchLabel,
    shouldShowMyLocationFieldsWithAddDefault,
    formatMyAddressRowBodyForDisplay,
    sanitizeMyJsonLocationRowForPersist,
} from '../utils/contactMyLocationFields';
import MyBranchAddressDisplayLine from './MyBranchAddressDisplayLine.vue';
import {
    ensureCachedMyCountryAndState,
    fetchCachedMyAllPostcodeOptions,
    getCachedMyAllPostcodeRawRows,
} from '../utils/myLocationOptionsCache';
import { useMyCorporateBranchAddressDelete } from '../composables/useMyCorporateBranchAddressDelete';
import type { MyRegisterAddressDeleteContext } from '@/features/register/MY/utils/persistMyRegisterContactAddresses';
import { shouldPersistMyAddressDelete } from '../utils/persistMyCorporateBranchAddresses';

type ContactInfoModel = Partial<Contact> & ContactInfo & {
    altAddressData?: {
        addressType?: 'B07001' | 'B07002';
        localAddress?: AddressInfo;
        foreignAddress?: AddressInfo;
    };
    /** MY: 상세는 SERVICE_ADDR_JSON_DATA·백엔드 alternate와 동기; 저장은 CONTACT_JSON_DATA 경로 유지 */
    altLocalAddress?: any;
    CONTACT_JSON_DATA?: string | Record<string, unknown> | null;
    SERVICE_ADDR_JSON_DATA?: string | Record<string, unknown> | null;
    BUSINESS_ADDR_JSON_DATA?: string | Record<string, unknown> | null;
    serviceAddressList?: any[];
    businessAddressList?: any[];
    /** MY Corporate: L_CONTACT_BRANCH 기반 주소 (프로필 컬럼 미사용) */
    USE_BRANCH_ADDRESSES?: boolean;
    /** MY alternate (Service) — emit / suppressedModelCache */
    ALT_ADDRESS_LINE_1?: string;
    ALT_ADDRESS_LINE_2?: string;
    ALT_ADDRESS_LINE_3?: string;
    ALT_COUNTRY?: string;
    ALT_STATE?: string;
    ALT_CITY?: string;
    ALT_POSTCODE?: string;
    ALT_COUNTRY_CODE?: string;
    ALT_STATE_CODE?: string;
    ALT_CITY_CODE?: string;
    ALT_POSTCODE_CODE?: string;
};

interface Props {
    modelValue?: ContactInfoModel;
    readonly?: boolean;
    requireEmail?: boolean;
    lockPrimaryEmail?: boolean;
    showSyncBadge?: boolean;
    forceUnlockPrimaryEmail?: boolean;
    manualOverride?: boolean;
    isIndividual?: boolean; // Individual Detail인지 여부
    addressType?: 'B07001' | 'B07002'; // Residential Address의 addressType (Registrable 페이지에서 사용)
    /** Contact Detail MY: SPC 다건 힌트 — Service / Business 라벨 옆 */
    showResidentialMultiHint?: boolean;
    residentialMultiHintText?: string;
    showServiceMultiHint?: boolean;
    serviceMultiHintText?: string;
    showBusinessMultiHint?: boolean;
    businessMultiHintText?: string;
    onReSyncAddressClick?: (group: SpcAddressConsolidationGroupTab) => void;
    /** Select Address / Show Address 팝업 */
    contactCd?: string;
    /** Entity_my: MAP repoint·저장 컨텍스트 */
    entityCd?: string;
    /** MY Corporate Show Address — Registered branch 목록 (ContactDetailPage) */
    residentialAddressListForShow?: any[];
    /** Entity_my Show Address — Service/Business contact 전체 풀 */
    serviceAddressShowPool?: any[];
    businessAddressShowPool?: any[];
    /** Registrable Create: 마운트·draft emit 루프 방지 (Save 시 getContactInfoPayloadForSave) */
    suppressParentEmit?: boolean;
    /** MY Contact Detail: 이름 외 필드(연락처·주소 등) 편집 허용 */
    myDetailEditableExceptName?: boolean;
    /** true — Service/Business 주소 섹션만 표시 (연락처 필드 숨김) */
    addressSectionsOnly?: boolean;
    /** Entity Register 주소 블록 사용 시 외부 Service/Business 주소 섹션 숨김 */
    hideAddressSections?: boolean;
    /** Beneficial Owner 등 — Service Address UI·저장 편집 제외 */
    hideServiceAddress?: boolean;
    /** Register BO 등 — 주소 행 삭제 버튼 숨김 */
    hideAddressDeleteButton?: boolean;
    /** Contact List / Registrable — Residential·Registered 대신 "Address" 라벨 (Show Address 등) */
    simpleAddressLabel?: boolean;
    /** Entity Detail — Service/Business 주소 Delete(−) 숨김 */
    hideAddressDelete?: boolean;
    /** MY Register — 역할별 주소 삭제 시 Register API 사용 */
    registerAddressDeleteContext?: MyRegisterAddressDeleteContext | null;
    /** Entity Detail Officer(Director/Secretary) — Business Address 블록 숨김 */
    hideBusinessAddressSection?: boolean;
    /** Entity officer detail — SG sync field indicators */
    syncChanges?: Map<string, { type: 'UPDATE' | 'CONFLICT'; before: unknown; after: unknown; note?: string }> | null;
}

const props = withDefaults(defineProps<Props>(), {
    readonly: false,
    requireEmail: false,
    lockPrimaryEmail: false,
    showSyncBadge: true,
    forceUnlockPrimaryEmail: false,
    manualOverride: false,
    isIndividual: false,
    addressType: undefined,
    showResidentialMultiHint: false,
    residentialMultiHintText: 'Multiple residential addresses exist for this contact.',
    showServiceMultiHint: false,
    serviceMultiHintText: 'Multiple service addresses exist for this contact.',
    showBusinessMultiHint: false,
    businessMultiHintText: 'Multiple business addresses exist for this contact.',
    contactCd: '',
    residentialAddressListForShow: () => [],
    serviceAddressShowPool: () => [],
    businessAddressShowPool: () => [],
    suppressParentEmit: false,
    myDetailEditableExceptName: false,
    addressSectionsOnly: false,
    hideAddressSections: false,
    hideServiceAddress: false,
    hideAddressDeleteButton: false,
    simpleAddressLabel: false,
    hideAddressDelete: false,
    registerAddressDeleteContext: null,
    hideBusinessAddressSection: false,
    syncChanges: null,
});

function syncChange(...fieldNames: string[]) {
    if (!props.syncChanges) return null;
    for (const f of fieldNames) {
        const hit = props.syncChanges.get(f);
        if (hit) return hit;
    }
    return null;
}

const emit = defineEmits<{
    'update:modelValue': [value: ContactInfoModel];
    refresh: [];
}>();

const { isDeleting, confirmAndDeleteBranchAddress } = useMyCorporateBranchAddressDelete({
    getContactCd: () => String(props.contactCd || contactCdForSelect.value || '').trim(),
    getRegisterAddressDeleteContext: () => props.registerAddressDeleteContext ?? null,
    getContactListFlatAddressMode: () => false,
    onRefreshed: async () => {
        emit('refresh');
    },
});

const normalizeRegisterType = (value?: string | null) => {
    const trimmed = String(value || '').trim();
    return trimmed ? trimmed.toUpperCase() : '';
};

// REGISTER_TYPE이 A06001인지 확인
const isBizFileRegistrant = computed(() => {
    const value = props.modelValue || {} as any;
    const registerType = normalizeRegisterType(
        value.CONTACT_REGISTER_TYPE || value.contactRegisterType
    );
    return registerType === 'A06001';
});

// MY인 경우 Alternative Address용 Entity 스타일 주소 데이터
const altMyAddressData = ref({
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    country: '',
    state: '',
    city: '',
    postcode: '',
});

// Alternative Address용 Country, State, City, Postcode 옵션 (MY인 경우)
const altCountryOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
const altStateOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
const altCityOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
/** Postcode 콤보 전용 — City 선택 시 API로만 채움(전국 목록 넣지 않음) */
const altPostcodeOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
/** 역검색·초기 역추적용 전국 Postcode — `loadAllAltPostcodeOptions`만 갱신, 콤보에 바인딩하지 않음 */
const altPostcodeAllOptionsCache = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
/** City 선택 등으로 altPostcodeOptions가 일부만 남아도 GUID→표시명 역조회 (한 줄 주소 표시용) */
const altPostcodeLookupByTotalCode = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);

function mergeMalaysiaPostcodeApiRowsIntoLookup(postcodes: any[]) {
    if (!Array.isArray(postcodes) || postcodes.length === 0) return;
    const map = new Map(altPostcodeLookupByTotalCode.value.map((o) => [o.TOTAL_CODE, o]));
    postcodes.forEach((postcode: any) => {
        const codeName = String(postcode.name || '').trim();
        const totalCode = String(postcode.code || postcode.name || '').trim();
        if (totalCode && codeName) {
            map.set(totalCode, { TOTAL_CODE: totalCode, CODE_NAME: codeName });
        }
    });
    altPostcodeLookupByTotalCode.value = Array.from(map.values());
}

/**
 * 표시명·역검색 시 코드 역조회용. 전국 캐시 + 현재 City 목록 + API lookup 병합.
 */
const mergedAltPostcodeAutocompleteItems = computed(() => {
    const map = new Map<string, { TOTAL_CODE: string; CODE_NAME: string }>();
    altPostcodeAllOptionsCache.value.forEach((o) => map.set(o.TOTAL_CODE, o));
    altPostcodeOptions.value.forEach((o) => map.set(o.TOTAL_CODE, o));
    altPostcodeLookupByTotalCode.value.forEach((o) => {
        if (o.TOTAL_CODE && !map.has(o.TOTAL_CODE)) {
            map.set(o.TOTAL_CODE, o);
        }
    });
    return Array.from(map.values());
});

/** City 미선택 시 Postcode 콤보(역방향) — 전국 캐시+lookup만 */
const altPostcodeReverseSearchItems = computed(() => {
    const map = new Map<string, { TOTAL_CODE: string; CODE_NAME: string }>();
    altPostcodeAllOptionsCache.value.forEach((o) => map.set(o.TOTAL_CODE, o));
    altPostcodeLookupByTotalCode.value.forEach((o) => {
        if (o.TOTAL_CODE && !map.has(o.TOTAL_CODE)) {
            map.set(o.TOTAL_CODE, o);
        }
    });
    return Array.from(map.values());
});

const getDisplayCodeName = (
    rawValue: any,
    options: Array<{ TOTAL_CODE: string; CODE_NAME: string }>
) => {
    const value = String(rawValue || '').trim();
    if (!value) return '';
    const found = options.find((opt) => opt.TOTAL_CODE === value);
    return found?.CODE_NAME || value;
};

/** Postcode는 Base Code가 아니라 Entity MY API(code=GUID, name=우편번호 숫자). 표시/바인딩은 merged 목록으로 역조회 */
function resolvePostcodeDisplayName(raw: any): string {
    const value = String(raw ?? '').trim();
    if (!value) return '';
    return getDisplayCodeName(value, mergedAltPostcodeAutocompleteItems.value);
}

/** MY CONTACT_JSON_DATA 저장 시 country/state/city는 TOTAL_CODE, post_code는 표시용 숫자 */
function mapMyJsonAddressRowForPersist(row: any): any {
    const o = { ...(row || {}) };
    const pcRaw = o.post_code ?? o.postcode ?? '';
    o.country = normalizeMyLocationFieldForPersist(o.country, altCountryOptions.value);
    o.state = normalizeMyLocationFieldForPersist(o.state, altStateOptions.value);
    o.city = normalizeMyLocationFieldForPersist(o.city, altCityOptions.value);
    o.stateCode = o.state;
    o.cityCode = o.city;
    const postcodeCode = normalizeMyLocationFieldForPersist(
        o.postcodeCode ?? o.postcode ?? o.post_code,
        mergedAltPostcodeAutocompleteItems.value,
    ) || String(o.postcodeCode ?? o.postcode ?? '').trim();
    o.postcodeCode = postcodeCode;
    o.post_code = resolvePostcodeDisplayName(pcRaw || postcodeCode);
    if ('postcode' in o) delete o.postcode;
    delete o._showLocationDefaultsOnAdd;
    delete o._countrySearch;
    // 수동 행에는 IsSync 미포함(동기화 슬롯만 IsSync:1 유지)
    const syncVal = o.IsSync ?? o.isSync;
    if (!(syncVal === 1 || syncVal === '1' || syncVal === true)) {
        delete o.IsSync;
        delete o.isSync;
    }
    return sanitizeMyJsonLocationRowForPersist(o, altCountryOptions.value);
}

// readonly일 때 표시할 값 (CODE_NAME) 계산 (Alternative Address용)
// readonly일 때는 JSON에서 받은 값(country, state, city, postcode)을 그대로 표시
const getAltCountryDisplayName = computed(() => {
    if (!altMyAddressData.value.country) return '';
    const option = altCountryOptions.value.find(opt => opt.TOTAL_CODE === altMyAddressData.value.country);
    return option ? option.CODE_NAME : altMyAddressData.value.country;
});

const getAltStateDisplayName = computed(() => {
    if (!altMyAddressData.value.state) return '';
    const option = altStateOptions.value.find(opt => opt.TOTAL_CODE === altMyAddressData.value.state);
    return option ? option.CODE_NAME : altMyAddressData.value.state;
});

const getAltCityDisplayName = computed(() => {
    if (!altMyAddressData.value.city) return '';
    const option = altCityOptions.value.find(opt => opt.TOTAL_CODE === altMyAddressData.value.city);
    return option ? option.CODE_NAME : altMyAddressData.value.city;
});

const getAltPostcodeDisplayName = computed(() => {
    return resolvePostcodeDisplayName(altMyAddressData.value.postcode);
});

/** 레거시 alt: City 선택됨 → 시티별만, City 없음 → 역방향용 전국 목록 */
const altLegacyPostcodeAutocompleteItems = computed(() => {
    if (altMyAddressData.value.city) {
        return altPostcodeOptions.value;
    }
    return altPostcodeReverseSearchItems.value;
});

const isAltMyAddressEditing = ref(false);
/** A06001(BizFile)이어도 Service/Business(JSON·레거시) 주소는 Edit 허용 */
const canEditAltMyAddress = computed(
    () => isJurisdictionMY.value && (!readonly.value || isBizFileRegistrant.value)
);
const mergedAltMyAddress = computed(() => {
    return joinAddressDisplayParts([
        altMyAddressData.value.addressLine1 || '',
        altMyAddressData.value.addressLine2 || '',
        altMyAddressData.value.addressLine3 || '',
        getAltPostcodeDisplayName.value || '',
        getAltCityDisplayName.value || '',
        getAltStateDisplayName.value || '',
        getAltCountryDisplayName.value || ''
    ]);
});
const hasMergedAltMyAddress = computed(() => mergedAltMyAddress.value.length > 0);
const showAltMyAddressSummary = computed(() => (
    isJurisdictionMY.value &&
    (readonly.value || (!isAltMyAddressEditing.value && hasMergedAltMyAddress.value))
));
const showAltMyAddressEditor = computed(() => isJurisdictionMY.value && !showAltMyAddressSummary.value);

const toggleAltMyAddressEdit = () => {
    if (!canEditAltMyAddress.value) return;
    isAltMyAddressEditing.value = !isAltMyAddressEditing.value;
};

const shouldShowAltLocationFields = computed(() =>
    shouldShowMyLocationFields(altMyAddressData.value.country, altCountryOptions.value)
);

const readonly = computed(() => {
    if (props.myDetailEditableExceptName) {
        return false;
    }
    if (isBizFileRegistrant.value) {
        return true;
    }
    return !!props.readonly;
});

// 필드별 readonly 계산
const isFieldReadonly = (fieldName: string) => {
    return readonly.value;
};

// 이메일 필드의 첫 번째 이메일이 비어있는지 확인
const isFirstEmailEmpty = computed(() => {
    const emails = contactInfo.value?.emails || [];
    return emails.length === 0 || !emails[0] || String(emails[0]).trim() === '';
});

// 이메일 필드별 readonly 계산
const isEmailFieldReadonly = (index: number) => {
    // 첫 번째 이메일(index === 0)인 경우
    if (index === 0) {
        // readonly 모드이면 readonly
        if (readonly.value) {
            return true;
        }
        // lockPrimaryEmail이 true이면 readonly
        if (isPrimaryEmailLocked(index)) {
            return true;
        }
        return false;
    }
    
    // 두 번째 이메일 이상인 경우
    if (readonly.value) {
        return true;
    }
    if (isPrimaryEmailLocked(index)) {
        return true;
    }
    return false;
};

// Country Code 표시 이름 (readonly일 때 사용)
const countryCodeDisplayName = computed(() => {
    const countryCode = contactInfo.value?.countryCode || '';
    if (!countryCode) return '';
    
    const option = countryCodeOptions.value.find((opt: any) => opt.TOTAL_CODE === countryCode);
    return option ? option.CODE_NAME : countryCode;
});

// 세션의 jurisdiction 확인 (SG가 아니면 block, street, building 입력 가능)
const sessionStore = useSessionStore();
const isJurisdictionSG = computed(() => {
  const customerInfo = sessionStore.getCustomerInfo();
  return customerInfo?.jurisdiction === 'SG';
});

// 세션의 JURISDICTION이 MY인지 확인
// 세션의 JURISDICTION이 MY인지 확인
// A06002 (Manual)일 때도 말레이시아 주소 필드를 표시 (CODE_NAME → TOTAL_CODE 변환 필요)
const isJurisdictionMY = computed(() => {
    const customerInfo = sessionStore.getCustomerInfo();
    const jurisdiction = customerInfo?.jurisdiction || '';
    return jurisdiction?.toUpperCase() === 'MY' || jurisdiction?.toUpperCase() === 'MALAYSIA';
});

/** MY: Contact 블록 Area Code 미표시 — Country Code / Mobile 열 너비 */
const contactPhoneRowColSm = computed(() => (isJurisdictionMY.value ? 6 : 4));

watch(() => isJurisdictionMY.value, (newValue) => {
    if (!newValue) {
        isAltMyAddressEditing.value = false;
        resetAllAddressEditStates();
    }
});

watch(() => readonly.value, (newValue) => {
    if (newValue) {
        isAltMyAddressEditing.value = false;
        resetAllAddressEditStates();
    }
});

const { createSyncT } = useTranslation();
const languageStore = useLanguageStore();
// 반응형 번역 함수를 computed로 생성
const t = computed(() => {
    const translate = createSyncT(languageStore.currentLanguage);
    return translate;
});

const parseContactJsonData = computed<Record<string, any>>(() => {
    const raw = (props.modelValue as any)?.CONTACT_JSON_DATA;
    if (!raw) return {};
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw);
        } catch (error) {
            console.warn('[ContactInfoForm] Failed to parse CONTACT_JSON_DATA:', error);
            return {};
        }
    }
    if (typeof raw === 'object') {
        return raw as Record<string, any>;
    }
    return {};
});

/** 빈 목록을 매번 `[]`로 만들면 v-autocomplete 패치 시 내부 오류(emitsOptions 등)가 날 수 있음 */
const EMPTY_ADDR_LIST: any[] = [];

const normalizeAddressArray = (value: unknown): any[] => {
    if (Array.isArray(value)) return value.length > 0 ? value : EMPTY_ADDR_LIST;
    if (value && typeof value === 'object') return [value];
    return EMPTY_ADDR_LIST;
};

const formatContactJsonAddress = (address: any): string => {
    return joinAddressDisplayParts([
        address?.street01 || address?.addressLine1 || '',
        address?.street02 || address?.addressLine2 || '',
        address?.street03 || address?.addressLine3 || '',
        address?.post_code || address?.postcode || address?.postalCode || '',
        address?.city || '',
        address?.state || '',
        address?.country || ''
    ]);
};

const isPrimaryAddressFlag = (value: unknown): boolean => {
    return value === 1 || value === '1' || value === true;
};

const isAddressSyncFlag = (addr: any): boolean => {
    if (!addr || typeof addr !== 'object') return false;
    const v = addr.IsSync ?? addr.isSync;
    return v === 1 || v === '1' || v === true;
};

/** service/business 코어 주소 필드가 모두 비었는지(IsSync=1인데 비어 있으면 Edit 허용) */
function contactJsonAddressCoreEmpty(addr: any): boolean {
    if (!addr || typeof addr !== 'object') return true;
    const s = (v: unknown) => String(v ?? '').trim();
    const line1 = s(addr.street01 ?? addr.addressLine1);
    const line2 = s(addr.street02 ?? addr.addressLine2);
    const line3 = s(addr.street03 ?? addr.addressLine3);
    const pc = s(addr.post_code ?? addr.postcode ?? addr.postalCode);
    const city = s(addr.city);
    const state = s(addr.state);
    const country = s(addr.country);
    return !line1 && !line2 && !line3 && !pc && !city && !state && !country;
}

/** 동기화된 주소이고 값이 있으면 Edit 불가(전부 빈칸일 때만 편집 가능) */
function contactJsonAddressSyncEditLocked(addr: any): boolean {
    return isAddressSyncFlag(addr) && !contactJsonAddressCoreEmpty(addr);
}

const filterPrimaryAddresses = (list: any[]): any[] => {
    if (!Array.isArray(list) || list.length === 0) return EMPTY_ADDR_LIST;
    const primaryOnly = list.filter((item) => {
        const flag = item?.isPrimaryAddress ?? item?.is_primary_address;
        return isPrimaryAddressFlag(flag);
    });
    return primaryOnly.length > 0 ? primaryOnly : EMPTY_ADDR_LIST;
};

/** primary 플래그가 없으면 전체 행 표시(프로필 컬럼 JSON 등) */
function filterPrimaryAddressesWithFallback(list: any[]): any[] {
    if (!Array.isArray(list) || list.length === 0) return EMPTY_ADDR_LIST;
    const primaryOnly = list.filter((item) => {
        const flag = item?.isPrimaryAddress ?? item?.is_primary_address;
        return isPrimaryAddressFlag(flag);
    });
    if (primaryOnly.length > 0) return primaryOnly;
    return list.map((r) => ({ ...(r || {}) }));
}

/** MY Corporate: L_CONTACT_BRANCH → API CONTACT_JSON_DATA / serviceAddressList */
const useBranchAddresses = computed(() => {
    const mv = props.modelValue as any;
    return Boolean(mv?.USE_BRANCH_ADDRESSES);
});

/** MY: L_GLOBAL_CONTACT_PROFILE.SERVICE_ADDR_JSON_DATA / BUSINESS_ADDR_JSON_DATA */
const serviceAddressesFromProfileColumnMy = computed(() => {
    const mv = props.modelValue as any;
    const raw = mv?.SERVICE_ADDR_JSON_DATA ?? mv?.service_addr_json_data;
    return normalizeAddressArray(extractAddressArrayFromProfileJsonColumn(raw, 'service_address'));
});
const businessAddressesFromProfileColumnMy = computed(() => {
    const mv = props.modelValue as any;
    const raw = mv?.BUSINESS_ADDR_JSON_DATA ?? mv?.business_addr_json_data;
    return normalizeAddressArray(extractAddressArrayFromProfileJsonColumn(raw, 'business_address'));
});

const serviceAddressesFromBranchMy = computed(() => {
    const mv = props.modelValue as any;
    if (Array.isArray(mv?.serviceAddressList) && mv.serviceAddressList.length > 0) {
        return normalizeAddressArray(mv.serviceAddressList);
    }
    if (Array.isArray(mv?.service_address) && mv.service_address.length > 0) {
        return normalizeAddressArray(mv.service_address);
    }
    return normalizeAddressArray(parseContactJsonData.value?.service_address);
});

const businessAddressesFromBranchMy = computed(() => {
    const mv = props.modelValue as any;
    if (Array.isArray(mv?.businessAddressList) && mv.businessAddressList.length > 0) {
        return normalizeAddressArray(mv.businessAddressList);
    }
    if (Array.isArray(mv?.business_address) && mv.business_address.length > 0) {
        return normalizeAddressArray(mv.business_address);
    }
    return normalizeAddressArray(parseContactJsonData.value?.business_address);
});

const serviceAddressListFromJson = computed(() => {
    if (isJurisdictionMY.value) {
        if (useBranchAddresses.value) {
            return filterPrimaryAddressesWithFallback(serviceAddressesFromBranchMy.value);
        }
        return filterPrimaryAddressesWithFallback(serviceAddressesFromProfileColumnMy.value);
    }
    // 비-MY: CONTACT_JSON_DATA.service_address
    return filterPrimaryAddresses(normalizeAddressArray(parseContactJsonData.value?.service_address));
});

/** Business Address — MY branch 또는 프로필 컬럼, 그 외 CONTACT_JSON_DATA */
const businessAddressListFromJson = computed(() => {
    if (isJurisdictionMY.value) {
        if (useBranchAddresses.value) {
            return filterPrimaryAddressesWithFallback(businessAddressesFromBranchMy.value);
        }
        return filterPrimaryAddressesWithFallback(businessAddressesFromProfileColumnMy.value);
    }
    return filterPrimaryAddresses(normalizeAddressArray(parseContactJsonData.value?.business_address));
});

/** 저장 merge용 raw */
const serviceAddressRawListFromJson = computed(() => {
    if (isJurisdictionMY.value && useBranchAddresses.value) {
        return serviceAddressesFromBranchMy.value;
    }
    if (isJurisdictionMY.value) {
        return serviceAddressesFromProfileColumnMy.value;
    }
    return normalizeAddressArray(parseContactJsonData.value?.service_address);
});

const businessAddressRawListFromJson = computed(() => {
    if (isJurisdictionMY.value && useBranchAddresses.value) {
        return businessAddressesFromBranchMy.value;
    }
    if (isJurisdictionMY.value) {
        return businessAddressesFromProfileColumnMy.value;
    }
    return normalizeAddressArray(parseContactJsonData.value?.business_address);
});

/**
 * 주소 값 필드만 (isPrimaryAddress / IsSync 제외) — 값 수정 여부로 Sync 해제 여부 판단
 */
function normalizeContactJsonAddressRowContentOnly(row: any): Record<string, string> {
    const x = row || {};
    const str = (v: unknown) => String(v ?? '').trim();
    let post_code = str(x.post_code ?? x.postcode ?? x.postalCode);
    let city = str(x.city);
    let state = str(x.state);
    let country = str(x.country);

    // MY: CODE_NAME·TOTAL_CODE 혼재 시 TOTAL_CODE 기준으로 비교(IsSync/편집 판정)
    if (isJurisdictionMY.value) {
        country = normalizeMyLocationFieldForPersist(country, altCountryOptions.value) || country;
        state = normalizeMyLocationFieldForPersist(state, altStateOptions.value) || state;
        city = normalizeMyLocationFieldForPersist(city, altCityOptions.value) || city;
        post_code = resolvePostcodeDisplayName(post_code) || post_code;
    }

    return {
        entity_cd: str(x.entity_cd ?? x.entityCd),
        street01: str(x.street01 ?? x.addressLine1),
        street02: str(x.street02 ?? x.addressLine2),
        street03: str(x.street03 ?? x.addressLine3),
        post_code,
        city,
        state,
        country,
        entity_name: str(x.entity_name),
        position: str(x.position),
        role: str(x.role)
    };
}

function contactJsonAddressContentEqual(a: any, b: any): boolean {
    return (
        JSON.stringify(normalizeContactJsonAddressRowContentOnly(a)) ===
        JSON.stringify(normalizeContactJsonAddressRowContentOnly(b))
    );
}

/** 값이 스냅샷 대비 바뀐 경우 수동 저장으로 간주 — IsSync 키는 제거(동기 슬롯만 서버가 IsSync:1 유지) */
function mapPrimaryDraftRowForSave(draft: any, index: number, snapshot: any[]): any {
    const snap = snapshot[index];
    const isNewRow = snap === undefined;
    const valueEdited = isNewRow || !contactJsonAddressContentEqual(draft, snap);
    const out = { ...(draft || {}) };
    if (valueEdited) {
        delete out.IsSync;
        delete out.isSync;
    }
    return out;
}

/**
 * 저장 시 service_address / business_address 공통:
 * primary 플래그가 없는 행(동기화·BO 등)은 그대로 두고,
 * primary draft는 값이 수정된 행에서 IsSync 키 제거(수동 행).
 */
function mergeContactJsonAddressArrayForSave(
    rawList: any[],
    primaryDraftList: any[],
    primarySnapshot: any[]
): any[] {
    const raw = Array.isArray(rawList) ? rawList : [];
    const nonPrimary = raw.filter((item) => {
        const flag = item?.isPrimaryAddress ?? item?.is_primary_address;
        return !isPrimaryAddressFlag(flag);
    });
    const primaryDraft = Array.isArray(primaryDraftList) ? primaryDraftList : [];
    const snap = Array.isArray(primarySnapshot) ? primarySnapshot : [];
    return [
        ...nonPrimary.map((r) => ({ ...(r || {}) })),
        ...primaryDraft.map((r, i) => mapPrimaryDraftRowForSave(r, i, snap))
    ];
}

/** MY Corporate branch: draft만 부모에 반영 (merge 비사용 — isPrimaryAddress 없어 중복 방지) */
function mapBranchAddressDraftListForEmit(draftList: any[]): any[] {
    return (Array.isArray(draftList) ? draftList : []).map((r) =>
        isJurisdictionMY.value ? mapMyJsonAddressRowForPersist({ ...r }) : { ...(r || {}) }
    );
}

function buildBranchAddressListsForEmit(): { serviceAddressList: any[]; businessAddressList: any[] } {
    return {
        serviceAddressList: mapBranchAddressDraftListForEmit(serviceAddressDraftList.value),
        businessAddressList: mapBranchAddressDraftListForEmit(businessAddressDraftList.value),
    };
}

const canEditAddressItem = computed(
    () =>
        (isJurisdictionMY.value || useBranchAddresses.value) &&
        (!readonly.value || isBizFileRegistrant.value),
);
const addressEditState = ref<Record<string, boolean>>({});

const useRegisterAddressCardStyle = computed(() => !!props.registerAddressDeleteContext);

const useRegisterBusinessOnlyLayout = computed(
    () =>
        useRegisterAddressCardStyle.value
        && !!props.addressSectionsOnly
        && !!props.hideServiceAddress,
);

const anyAddressEditing = () => Object.values(addressEditState.value).some(Boolean);
const isSyncingAddressDraftsFromProps = ref(false);
const serviceAddressDraftList = ref<any[]>([]);
const businessAddressDraftList = ref<any[]>([]);
/** merge 저장 시 “마지막 props 동기화 시점” primary 행 — 변경 없으면 수동 플래그 부여 안 함 */
const servicePrimarySnapshotForMerge = ref<any[]>([]);
const businessPrimarySnapshotForMerge = ref<any[]>([]);

const mapMyJsonRowForForm = (address: any) => {
    if (!isJurisdictionMY.value) return { ...(address || {}) };
    const row = normalizeMyLocationRowForForm(address || {}, {
        countryOpts: altCountryOptions.value,
        stateOpts: altStateOptions.value,
        cityOpts: altCityOptions.value,
    });
    row._countrySearch = countryCodeToSearchLabel(row.country, altCountryOptions.value);
    row.alias = String(row.alias ?? row.ALIAS ?? '').trim();
    return row;
};

/** 프로필/JSON primary 목록이 실제로 바뀐 경우에만 draft reset (참조만 바뀐 emit 무시) */
function addressListSyncKey(list: any[]): string {
    return JSON.stringify(
        (list || []).map((a) => normalizeContactJsonAddressRowContentOnly(a))
    );
}

const lastServiceAddressJsonSyncKey = ref<string | null>(null);
const lastBusinessAddressJsonSyncKey = ref<string | null>(null);

const syncServiceAddressDraftList = (options?: { force?: boolean }) => {
    isSyncingAddressDraftsFromProps.value = true;
    try {
        const primaries = serviceAddressListFromJson.value.map((address) => mapMyJsonRowForForm(address));
        const current = serviceAddressDraftList.value;
        if (
            props.registerAddressDeleteContext
            && !options?.force
            && !anyAddressEditing()
            && primaries.length > 0
            && current.length <= primaries.length
            && lastServiceAddressJsonSyncKey.value === addressListSyncKey(current)
        ) {
            return;
        }
        const extraRows =
            !options?.force && current.length > primaries.length
                ? current.slice(primaries.length).map((r) => ({ ...(r || {}) }))
                : [];
        serviceAddressDraftList.value = [...primaries, ...extraRows];
        servicePrimarySnapshotForMerge.value = primaries.map((a) => ({ ...(a || {}) }));
    } finally {
        nextTick(() => {
            isSyncingAddressDraftsFromProps.value = false;
        });
    }
};

const syncBusinessAddressDraftList = (options?: { force?: boolean }) => {
    isSyncingAddressDraftsFromProps.value = true;
    try {
        const primaries = businessAddressListFromJson.value.map((address) => mapMyJsonRowForForm(address));
        const current = businessAddressDraftList.value;
        if (
            props.registerAddressDeleteContext
            && !options?.force
            && !anyAddressEditing()
            && primaries.length > 0
            && current.length <= primaries.length
            && lastBusinessAddressJsonSyncKey.value === addressListSyncKey(current)
        ) {
            return;
        }
        const extraRows =
            !options?.force && current.length > primaries.length
                ? current.slice(primaries.length).map((r) => ({ ...(r || {}) }))
                : [];
        businessAddressDraftList.value = [...primaries, ...extraRows];
        businessPrimarySnapshotForMerge.value = primaries.map((a) => ({ ...(a || {}) }));
    } finally {
        nextTick(() => {
            isSyncingAddressDraftsFromProps.value = false;
        });
    }
};

/** MY 옵션 로드 후 코드 표시만 갱신 — draft 행 수는 유지(Add 직후 sync로 비워지지 않음) */
const remapServiceAddressDraftRowsInPlace = () => {
    if (serviceAddressDraftList.value.length === 0) return;
    serviceAddressDraftList.value = serviceAddressDraftList.value.map((address) =>
        mapMyJsonRowForForm(address)
    );
};

const remapBusinessAddressDraftRowsInPlace = () => {
    if (businessAddressDraftList.value.length === 0) return;
    businessAddressDraftList.value = businessAddressDraftList.value.map((address) =>
        mapMyJsonRowForForm(address)
    );
};

watch(
    serviceAddressListFromJson,
    (list) => {
        const key = addressListSyncKey(list);
        if (key === lastServiceAddressJsonSyncKey.value) return;
        lastServiceAddressJsonSyncKey.value = key;
        syncServiceAddressDraftList();
    },
    { immediate: true }
);

watch(
    businessAddressListFromJson,
    (list) => {
        const key = addressListSyncKey(list);
        if (key === lastBusinessAddressJsonSyncKey.value) return;
        lastBusinessAddressJsonSyncKey.value = key;
        syncBusinessAddressDraftList();
    },
    { immediate: true }
);

watch(
    () =>
        isJurisdictionMY.value
            ? [
                  altCountryOptions.value.length,
                  altStateOptions.value.length,
                  altCityOptions.value.length,
              ]
            : [],
    () => {
        if (!isJurisdictionMY.value) return;
        remapServiceAddressDraftRowsInPlace();
        remapBusinessAddressDraftRowsInPlace();
    }
);

const getAddressEditKey = (type: 'service' | 'business', index: number) => `${type}-${index}`;

/** v-text-field :rules에 매번 `[]`를 넘기면 동일한 패치 이슈가 날 수 있음 */
const EMPTY_EMAIL_RULES: any[] = [];

/** JSON 주소 콤보: City/Postcode는 레거시와 동일 전역 altCityOptions·altPostcodeOptions(시티 한정) 사용 */
const isInitializingJsonAddressLocation = ref(false);
const suppressJsonAddressCascade = ref(false);

async function hydrateContactJsonAddressFields(address: any) {
    if (!isJurisdictionMY.value || !address) return;
    const stateCode = resolveTotalCodeFromField(address.state, altStateOptions.value);
    if (!stateCode) return;
    await loadAltCitiesByState(stateCode);
    const cityCode = resolveTotalCodeFromField(address.city, altCityOptions.value);
    if (cityCode) {
        await loadAltPostcodeOptions(cityCode, true);
    }
}

/** CONTACT_JSON service/business 행: Malaysia일 때만 State/City/Postcode (+ Add 시 country 비어 있으면 기본 표시) */
const serviceRowShowLocationFields = computed(() =>
    serviceAddressDraftList.value.map((row) =>
        shouldShowMyLocationFieldsWithAddDefault(
            row?.country,
            altCountryOptions.value,
            !!(row as any)?._showLocationDefaultsOnAdd
        )
    )
);
const businessRowShowLocationFields = computed(() =>
    businessAddressDraftList.value.map((row) =>
        shouldShowMyLocationFieldsWithAddDefault(
            row?.country,
            altCountryOptions.value,
            !!(row as any)?._showLocationDefaultsOnAdd
        )
    )
);

/** Country 변경 시 MY 하위 필드·공유 콤보 정리 (Malaysia 전환 포함 이전 값 제거) */
function onContactJsonAddressCountryUpdate(address: any, newCountry: string | null | undefined) {
    if (!address) return;
    const c = newCountry != null ? String(newCountry) : '';
    const prevCountry =
        address._countryChangeBaseline !== undefined
            ? String(address._countryChangeBaseline)
            : resolveCountryTotalCode(address.country, altCountryOptions.value);
    if (c && typeof address === 'object') {
        delete address._showLocationDefaultsOnAdd;
    }
    if (
        applyCountryChangeClearsSubLocation(
            address,
            prevCountry,
            c,
            altCountryOptions.value
        )
    ) {
        altPostcodeOptions.value = [];
        altCityOptions.value = [];
    }
    if (typeof address === 'object') {
        address._countryChangeBaseline = resolveCountryTotalCode(
            c,
            altCountryOptions.value
        );
    }
}

function syncJsonAddressCountrySearch(address: any) {
    if (!address || typeof address !== 'object') return;
    address._countrySearch = countryCodeToSearchLabel(address.country, altCountryOptions.value);
}

function onJsonAddressCountryFocus(address: any) {
    if (address && typeof address === 'object') {
        address._countryChangeBaseline = resolveCountryTotalCode(
            address.country,
            altCountryOptions.value
        );
    }
    syncJsonAddressCountrySearch(address);
}

function onJsonAddressCountrySelect(address: any, newCountry: string | null | undefined) {
    onContactJsonAddressCountryUpdate(address, newCountry);
    if (!address || typeof address !== 'object') return;
    address._countrySearch = newCountry
        ? countryCodeToSearchLabel(newCountry, altCountryOptions.value)
        : '';
}

const altCountrySearch = ref('');

function syncAltCountrySearchFromModel() {
    altCountrySearch.value = countryCodeToSearchLabel(
        altMyAddressData.value.country,
        altCountryOptions.value
    );
}

function onAltCountryFocus() {
    syncAltCountrySearchFromModel();
}

function onAltMyAddressCountryUpdate(newCountry: string | null | undefined) {
    const c = newCountry != null ? String(newCountry) : '';
    const prevCountry = resolveCountryTotalCode(
        altMyAddressData.value.country,
        altCountryOptions.value
    );
    altCountrySearch.value = c
        ? countryCodeToSearchLabel(c, altCountryOptions.value)
        : '';
    if (
        applyCountryChangeClearsSubLocation(
            altMyAddressData.value as Record<string, unknown>,
            prevCountry,
            c,
            altCountryOptions.value
        )
    ) {
        altPostcodeOptions.value = [];
        altCityOptions.value = [];
    }
}

async function onContactJsonAddressStateSelected(address: any, newState: string | null | undefined) {
    if (!address) return;
    if (!isJurisdictionMY.value && !useBranchAddresses.value) return;
    if (suppressJsonAddressCascade.value) return;
    const st = newState != null ? String(newState) : '';
    if (st && address.country !== 'B15135') {
        address.country = 'B15135';
    }
    address.city = '';
    address.post_code = '';
    address.postcode = '';
    address.postcodeCode = '';
    altPostcodeOptions.value = [];
    altCityOptionsForStateCode.value = '';
    if (st) {
        const stateApi = resolveTotalCodeFromField(st, altStateOptions.value) || st;
        await loadAltCitiesByState(stateApi);
    } else {
        altCityOptions.value = [];
        if (altPostcodeAllOptionsCache.value.length === 0) {
            await loadAllAltPostcodeOptions();
        }
    }
}

async function onContactJsonAddressCitySelected(address: any, newCity: string | null | undefined) {
    if (!address) return;
    if (!isJurisdictionMY.value && !useBranchAddresses.value) return;
    if (suppressJsonAddressCascade.value) return;
    const ct = newCity != null ? String(newCity) : '';
    if (ct && address.country !== 'B15135') {
        address.country = 'B15135';
    }
    address.post_code = '';
    address.postcode = '';
    address.postcodeCode = '';
    if (ct) {
        const cityApi = resolveTotalCodeFromField(ct, altCityOptions.value) || ct;
        await loadAltPostcodeOptions(cityApi, false);
    } else {
        altPostcodeOptions.value = [];
        if (altPostcodeAllOptionsCache.value.length === 0) {
            await loadAllAltPostcodeOptions();
        }
    }
}

function queueContactJsonAddressState(address: any, v: string | null | undefined) {
    void onContactJsonAddressStateSelected(address, v).catch((err) =>
        console.error('[ContactInfoForm] JSON address state selection', err)
    );
}

function queueContactJsonAddressCity(address: any, v: string | null | undefined) {
    void onContactJsonAddressCitySelected(address, v).catch((err) =>
        console.error('[ContactInfoForm] JSON address city selection', err)
    );
}

function queueContactJsonAddressPostcode(address: any) {
    void handleArrayAddressPostcodeChange(address).catch((err) =>
        console.error('[ContactInfoForm] JSON address postcode selection', err)
    );
}

/** JSON 주소: City 있으면 시티별 `altPostcodeOptions`, 없으면 역방향용 전국 목록 */
function getContactJsonPostcodeComboItems(address: any) {
    const cityRaw = address?.city;
    const cityCode =
        resolveTotalCodeFromField(cityRaw, altCityOptions.value) || String(cityRaw || '').trim();
    if (cityCode) {
        return altPostcodeOptions.value;
    }
    return altPostcodeReverseSearchItems.value;
}

const isAddressEditing = (type: 'service' | 'business', index: number) => {
    return !!addressEditState.value[getAddressEditKey(type, index)];
};

/** A06001이어도 Edit 모드일 때는 Service/Business 주소 입력 가능 */
function isJsonAddressRowReadonly(type: 'service' | 'business', index: number): boolean {
    const addr =
        type === 'service' ? serviceAddressDraftList.value[index] : businessAddressDraftList.value[index];
    const editing = isAddressEditing(type, index);
    // Edit 화면에 들어온 뒤에는 입력 가능(동기·값 있음은 Edit 버튼으로 막음, 빈 동기만 진입)
    if (editing) {
        if (!readonly.value) return false;
        if (isBizFileRegistrant.value) return false;
        return true;
    }
    if (contactJsonAddressSyncEditLocked(addr)) return true;
    if (!readonly.value) return false;
    return true;
}

const isLegacyAltMyAddressLineReadonly = computed(() => {
    if (!readonly.value) return false;
    if (isBizFileRegistrant.value && isAltMyAddressEditing.value) return false;
    return true;
});

const toggleAddressEdit = (type: 'service' | 'business', index: number) => {
    if (!canEditAddressItem.value) return;
    const addr =
        type === 'service' ? serviceAddressDraftList.value[index] : businessAddressDraftList.value[index];
    const key = getAddressEditKey(type, index);
    const next = !addressEditState.value[key];
    if (next && contactJsonAddressSyncEditLocked(addr)) return;
    addressEditState.value = { ...addressEditState.value, [key]: next };

    if (next) {
        const addr =
            type === 'service'
                ? serviceAddressDraftList.value[index]
                : businessAddressDraftList.value[index];
        void hydrateContactJsonAddressFields(addr).catch((err) =>
            console.error('[ContactInfoForm] hydrateContactJsonAddressFields', err)
        );
    } else if (type === 'service') {
        const row = serviceAddressDraftList.value[index];
        if (row?._isNewDraftRow) {
            serviceAddressDraftList.value = serviceAddressDraftList.value.filter((_, i) => i !== index);
            lastServiceAddressJsonSyncKey.value = addressListSyncKey(serviceAddressDraftList.value);
            clearAddressEditStateForType('service');
            return;
        }
        syncServiceAddressDraftList({ force: true });
    } else {
        const row = businessAddressDraftList.value[index];
        if (row?._isNewDraftRow) {
            businessAddressDraftList.value = businessAddressDraftList.value.filter((_, i) => i !== index);
            lastBusinessAddressJsonSyncKey.value = addressListSyncKey(businessAddressDraftList.value);
            clearAddressEditStateForType('business');
            return;
        }
        syncBusinessAddressDraftList({ force: true });
    }
};

const contactCdForSelect = computed(() => String(props.contactCd || '').trim());

const showBranchAddressShowDialog = ref(false);
const branchShowTarget = ref<{ type: 'service' | 'business'; index: number } | null>(null);
const branchShowDialogItems = ref<BranchAddressShowItem[]>([]);
/** Show Address 빌드·오픈 중 contactInfo emit 금지 (Maximum recursive updates 방지) */
const isSuppressingContactInfoEmit = ref(false);
const showServiceSelectDialog = ref(false);
const showBusinessSelectDialog = ref(false);
const serviceSelectEditIndex = ref<number | null>(null);
const businessSelectEditIndex = ref<number | null>(null);

const isAddressPickerDialogOpen = () =>
    isSuppressingContactInfoEmit.value ||
    showBranchAddressShowDialog.value ||
    showServiceSelectDialog.value ||
    showBusinessSelectDialog.value;

watch(showBranchAddressShowDialog, (open) => {
    if (!open) {
        isSuppressingContactInfoEmit.value = false;
        branchShowTarget.value = null;
    }
});

const entityCdForBranch = computed(() => String(props.entityCd || '').trim());

function resolveBranchRowsForShow(type: 'service' | 'business'): any[] {
    if (entityCdForBranch.value) {
        const pool =
            type === 'service' ? props.serviceAddressShowPool : props.businessAddressShowPool;
        if (Array.isArray(pool) && pool.length > 0) {
            return pool.map((r: any) => ({ ...r }));
        }
    }
    const mv = props.modelValue as any;
    if (type === 'service') {
        if (useBranchAddresses.value) {
            if (Array.isArray(mv?.serviceAddressList) && mv.serviceAddressList.length > 0) {
                return mv.serviceAddressList.map((r: any) => ({ ...r }));
            }
            return serviceAddressesFromBranchMy.value.map((r: any) => ({ ...r }));
        }
        if (Array.isArray(mv?.service_address) && mv.service_address.length > 0) {
            return mv.service_address.map((r: any) => ({ ...r }));
        }
        return serviceAddressesFromBranchMy.value.map((r: any) => ({ ...r }));
    }
    if (useBranchAddresses.value) {
        if (Array.isArray(mv?.businessAddressList) && mv.businessAddressList.length > 0) {
            return mv.businessAddressList.map((r: any) => ({ ...r }));
        }
        return businessAddressesFromBranchMy.value.map((r: any) => ({ ...r }));
    }
    if (Array.isArray(mv?.business_address) && mv.business_address.length > 0) {
        return mv.business_address.map((r: any) => ({ ...r }));
    }
    return businessAddressesFromBranchMy.value.map((r: any) => ({ ...r }));
}

const corporateBranchShowLabels = computed(() => ({
    residential: props.simpleAddressLabel
        ? (t.value('contact.Address') || 'Address')
        : props.isIndividual
            ? (t.value('contact.Residential Address') || 'Residential Address')
            : (t.value('contact.Registered Address') || 'Registered Address'),
    service: t.value('contact.Service Address') || 'Service Address',
    business: t.value('contact.Business Address') || 'Business Address',
}));

const openBranchShowAddress = (type: 'service' | 'business', index: number) => {
    if (!useBranchAddresses.value || !canEditAddressItem.value || !isAddressEditing(type, index)) return;
    isSuppressingContactInfoEmit.value = true;
    branchShowTarget.value = { type, index };
    try {
        branchShowDialogItems.value = buildAllCorporateBranchShowItems({
            residential: Array.isArray(props.residentialAddressListForShow)
                ? props.residentialAddressListForShow
                : [],
            service: resolveBranchRowsForShow('service'),
            business: resolveBranchRowsForShow('business'),
            labels: corporateBranchShowLabels.value,
        });
        void nextTick(() => {
            showBranchAddressShowDialog.value = true;
        });
    } catch (err) {
        console.error('[ContactInfoForm] openBranchShowAddress failed', err);
        isSuppressingContactInfoEmit.value = false;
        branchShowTarget.value = null;
    }
};

const onBranchShowRowSelected = async (row: Record<string, unknown>) => {
    const target = branchShowTarget.value;
    if (!target) return;
    const key = getAddressEditKey(target.type, target.index);
    if (!addressEditState.value[key]) {
        addressEditState.value = { ...addressEditState.value, [key]: true };
    }
    await loadAltCountryOptions();
    await loadAltStateOptions();
    const draftList =
        target.type === 'service' ? serviceAddressDraftList.value : businessAddressDraftList.value;
    const draft = draftList[target.index];
    if (!draft) return;
    const applyOpts = entityCdForBranch.value
        ? { repointEntityMapBranch: true, entityCd: entityCdForBranch.value }
        : { preserveDraftBranchIdentity: true };
    applyBranchAddressRowToDraft(
        draft,
        row,
        {
            countryOpts: altCountryOptions.value,
            stateOpts: altStateOptions.value,
            cityOpts: altCityOptions.value,
        },
        applyOpts,
    );
    draft._countrySearch = countryCodeToSearchLabel(draft.country, altCountryOptions.value);
    void hydrateContactJsonAddressFields(draft).catch(() => {});
};

const openJsonSelectAddress = (type: 'service' | 'business', index: number) => {
    if (useBranchAddresses.value || !isJurisdictionMY.value || !contactCdForSelect.value) return;
    if (!canEditAddressItem.value) return;
    if (type === 'service') {
        serviceSelectEditIndex.value = index;
        showServiceSelectDialog.value = true;
    } else {
        businessSelectEditIndex.value = index;
        showBusinessSelectDialog.value = true;
    }
};

async function applyJsonAddressCandidateToDraft(
    type: 'service' | 'business',
    index: number,
    candidate: ContactAddressCandidate,
    expectedType: ContactAddressTypeCode
) {
    if (candidate.addressType !== expectedType) return;
    await loadAltCountryOptions();
    await loadAltStateOptions();
    const mapped = mapCandidateToMyAddressData(candidate.address);
    const draftList =
        type === 'service' ? serviceAddressDraftList.value : businessAddressDraftList.value;
    const draft = draftList[index];
    if (!draft) return;
    draft.street01 = mapped.addressLine1;
    draft.street02 = mapped.addressLine2;
    draft.street03 = mapped.addressLine3;
    draft.country = mapped.country;
    draft.state = mapped.state;
    draft.city = mapped.city;
    draft.post_code = mapped.postcode;
    const normalized = mapMyJsonRowForForm(draft);
    Object.assign(draft, normalized);
    void hydrateContactJsonAddressFields(draft).catch(() => {});
}

const onServiceAddressCandidateSelected = (candidate: ContactAddressCandidate) => {
    const index = serviceSelectEditIndex.value;
    if (index == null) return;
    const key = getAddressEditKey('service', index);
    if (!addressEditState.value[key]) {
        addressEditState.value = { ...addressEditState.value, [key]: true };
    }
    void applyJsonAddressCandidateToDraft('service', index, candidate, 'M04002');
};

const onBusinessAddressCandidateSelected = (candidate: ContactAddressCandidate) => {
    const index = businessSelectEditIndex.value;
    if (index == null) return;
    const key = getAddressEditKey('business', index);
    if (!addressEditState.value[key]) {
        addressEditState.value = { ...addressEditState.value, [key]: true };
    }
    void applyJsonAddressCandidateToDraft('business', index, candidate, 'M04003');
};

/** CONTACT_JSON_DATA에 business_address가 없을 때 Edit으로 첫 행 생성 (primary=1, 수동 — IsSync 없음) */
const createDefaultBusinessAddressRow = () => ({
    isPrimaryAddress: 1,
    alias: '',
    street01: '',
    street02: '',
    street03: '',
    post_code: '',
    city: '',
    state: '',
    country: '',
    _countrySearch: '',
    _showLocationDefaultsOnAdd: true,
    _isNewDraftRow: true,
});

const createDefaultServiceAddressRow = () => ({
    isPrimaryAddress: 1,
    alias: '',
    street01: '',
    street02: '',
    street03: '',
    post_code: '',
    city: '',
    state: '',
    country: '',
    _countrySearch: '',
    _showLocationDefaultsOnAdd: true,
    _isNewDraftRow: true,
});

/** 일반 비즈니스 주소 N건: primary 행을 draft에 추가 (저장 시 mergeContactJsonAddressArrayForSave와 동일 규칙) */
const addBusinessAddressRow = async () => {
    if (!canEditAddressItem.value) return;
    await loadAltCountryOptions();
    await loadAltStateOptions();
    void loadAllAltPostcodeOptions().catch(() => {});
    const row = createDefaultBusinessAddressRow();
    const newIndex = businessAddressDraftList.value.length;
    businessAddressDraftList.value = [...businessAddressDraftList.value, row];
    lastBusinessAddressJsonSyncKey.value = addressListSyncKey(businessAddressDraftList.value);
    addressEditState.value = {
        ...addressEditState.value,
        [getAddressEditKey('business', newIndex)]: true
    };
    void hydrateContactJsonAddressFields(row).catch((err) =>
        console.error('[ContactInfoForm] hydrateContactJsonAddressFields (business add)', err)
    );
};

const addServiceAddressRow = async () => {
    if (!canEditAddressItem.value) return;
    await loadAltCountryOptions();
    await loadAltStateOptions();
    void loadAllAltPostcodeOptions().catch(() => {});
    const row = createDefaultServiceAddressRow();
    const newIndex = serviceAddressDraftList.value.length;
    serviceAddressDraftList.value = [...serviceAddressDraftList.value, row];
    lastServiceAddressJsonSyncKey.value = addressListSyncKey(serviceAddressDraftList.value);
    addressEditState.value = {
        ...addressEditState.value,
        [getAddressEditKey('service', newIndex)]: true
    };
    void hydrateContactJsonAddressFields(row).catch((err) =>
        console.error('[ContactInfoForm] hydrateContactJsonAddressFields (service add)', err)
    );
};

const canDeleteBranchAddressRow = (_address?: any) => canEditAddressItem.value;

function clearAddressEditStateForType(type: 'service' | 'business') {
    const prefix = `${type}-`;
    const next: Record<string, boolean> = {};
    Object.entries(addressEditState.value).forEach(([k, v]) => {
        if (!k.startsWith(prefix)) next[k] = v;
    });
    addressEditState.value = next;
}

function getResidentialListForBranchPersist(): any[] {
    const info = (props.modelValue ?? contactInfo.value) as any;
    const ra = info?.residential_address;
    if (Array.isArray(ra)) return ra.map((r: any) => ({ ...r }));
    if (ra && typeof ra === 'object') return [{ ...ra }];
    return [];
}

function buildFullBranchListsForPersist(serviceList: any[], businessList: any[]) {
    return {
        residentialAddressList: getResidentialListForBranchPersist(),
        serviceAddressList: mapBranchAddressDraftListForEmit(serviceList),
        businessAddressList: mapBranchAddressDraftListForEmit(businessList),
    };
}

const removeServiceAddressRow = async (index: number) => {
    if (props.hideAddressDelete) return;
    if (!canEditAddressItem.value) return;
    const addr = serviceAddressDraftList.value[index];
    const newService = serviceAddressDraftList.value.filter((_, i) => i !== index);

    if (useBranchAddresses.value || props.registerAddressDeleteContext) {
        if (!canDeleteBranchAddressRow(addr)) return;
        const lists = buildFullBranchListsForPersist(newService, businessAddressDraftList.value);
        const persistOnDelete = shouldPersistMyAddressDelete({
            registerAddressDeleteContext: props.registerAddressDeleteContext,
            contactCd: props.contactCd || contactCdForSelect.value,
            useBranchAddresses: useBranchAddresses.value,
            row: addr,
        });
        const ok = await confirmAndDeleteBranchAddress(
            `service-${index}`,
            lists,
            persistOnDelete,
        );
        if (!ok) return;
    }

    serviceAddressDraftList.value = newService;
    lastServiceAddressJsonSyncKey.value = addressListSyncKey(newService);
    clearAddressEditStateForType('service');
    emitPayloadIfChanged(buildEmitPayload(contactInfo.value));
};

const removeBusinessAddressRow = async (index: number) => {
    if (props.hideAddressDelete) return;
    if (!canEditAddressItem.value) return;
    const addr = businessAddressDraftList.value[index];
    const newBusiness = businessAddressDraftList.value.filter((_, i) => i !== index);

    if (useBranchAddresses.value || props.registerAddressDeleteContext) {
        if (!canDeleteBranchAddressRow(addr)) return;
        const lists = buildFullBranchListsForPersist(serviceAddressDraftList.value, newBusiness);
        const persistOnDelete = shouldPersistMyAddressDelete({
            registerAddressDeleteContext: props.registerAddressDeleteContext,
            contactCd: props.contactCd || contactCdForSelect.value,
            useBranchAddresses: useBranchAddresses.value,
            row: addr,
        });
        const ok = await confirmAndDeleteBranchAddress(
            `business-${index}`,
            lists,
            persistOnDelete,
        );
        if (!ok) return;
    }

    businessAddressDraftList.value = newBusiness;
    lastBusinessAddressJsonSyncKey.value = addressListSyncKey(newBusiness);
    clearAddressEditStateForType('business');
    emitPayloadIfChanged(buildEmitPayload(contactInfo.value));
};

const formatContactJsonAddressDisplay = (address: any): string =>
    formatMyAddressRowBodyForDisplay(address, {
        countryOpts: altCountryOptions.value,
        stateOpts: altStateOptions.value,
        cityOpts: altCityOptions.value,
        postcodeOpts: mergedAltPostcodeAutocompleteItems.value,
    });

const handleArrayAddressPostcodeChange = async (address: any) => {
    if (!address) return;
    const postcodeValue = String(address.post_code || address.postcode || '').trim();
    if (!postcodeValue) return;

    if (address.country !== 'B15135') {
        address.country = 'B15135';
    }

    if (altPostcodeAllOptionsCache.value.length === 0) {
        await loadAllAltPostcodeOptions();
    }

    const merged = mergedAltPostcodeAutocompleteItems.value;
    const postcodeNumber =
        merged.find((opt) => opt.TOTAL_CODE === postcodeValue || opt.CODE_NAME === postcodeValue)?.CODE_NAME ||
        altPostcodeOptions.value.find((opt) => opt.TOTAL_CODE === postcodeValue || opt.CODE_NAME === postcodeValue)?.CODE_NAME ||
        (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(postcodeValue)
            ? ''
            : postcodeValue);
    if (!postcodeNumber) return;

    const currentStateCode =
        resolveTotalCodeFromField(address.state, altStateOptions.value) || (address.state as string) || null;
    const currentCityCode =
        resolveTotalCodeFromField(address.city, altCityOptions.value) || (address.city as string) || null;

    const locationInfo = await findAltStateAndCityByPostcode(
        postcodeNumber,
        currentStateCode,
        currentCityCode
    );

    if (!locationInfo?.stateCode || !locationInfo.cityCode) return;

    suppressJsonAddressCascade.value = true;
    address._suppressLocationCascade = true;
    isInitializingJsonAddressLocation.value = true;
    try {
        address.state = locationInfo.stateCode;
        await loadAltCitiesByState(locationInfo.stateCode);
        address.city = locationInfo.cityCode;
        await loadAltPostcodeOptions(locationInfo.cityCode, true);
        const keepPc =
            merged.find(
                (opt) => opt.TOTAL_CODE === postcodeValue || opt.CODE_NAME === postcodeNumber,
            )?.TOTAL_CODE || postcodeValue;
        address.post_code = keepPc;
        address.postcodeCode = keepPc;
    } finally {
        await nextTick();
        address._suppressLocationCascade = false;
        suppressJsonAddressCascade.value = false;
        isInitializingJsonAddressLocation.value = false;
    }
};

const resetAllAddressEditStates = () => {
    addressEditState.value = {};
    syncServiceAddressDraftList({ force: true });
    syncBusinessAddressDraftList({ force: true });
};

const showLegacyServiceEditor = computed(() => serviceAddressListFromJson.value.length === 0);

/** JSON·draft·레거시 alternate 모두 없을 때 Business와 동일 '-' + Add 자리 */
const showServiceAddressEmptyPlaceholder = computed(
    () =>
        isJurisdictionMY.value &&
        showLegacyServiceEditor.value &&
        serviceAddressDraftList.value.length === 0 &&
        !hasMergedAltMyAddress.value
);

const showLegacyServiceSummary = computed(
    () =>
        showLegacyServiceEditor.value &&
        serviceAddressDraftList.value.length === 0 &&
        readonly.value &&
        hasMergedAltMyAddress.value &&
        !(isBizFileRegistrant.value && isAltMyAddressEditing.value)
);

const showLegacyServiceEditForm = computed(() => {
    if (serviceAddressListFromJson.value.length > 0 || serviceAddressDraftList.value.length > 0) return false;
    if (!showLegacyServiceEditor.value) return false;
    if (!hasMergedAltMyAddress.value) return false;
    if (!readonly.value) return true;
    return isBizFileRegistrant.value && isAltMyAddressEditing.value;
});

// 레거시 Service(배열 없음) + 비읽기 + 주소 없음: 요약이 false라 편집 UI가 이미 열린다.
// hasMerged가 옵션 로드 전에 false일 때 immediate로 편집을 켜면 로드 후에도 편집이 풀리지 않는다.

// MY Alternative Address 옵션 로드 함수 (재사용 가능하도록 분리)
const loadAltMyOptions = async () => {
    if (props.suppressParentEmit) return;
    if (isSyncingAltMyAddress.value) return; // 무한 루프 방지
    isSyncingAltMyAddress.value = true;
    
    try {
        console.log('✅ [ContactInfoForm] MY jurisdiction detected, loading alt address options...');
        
        // readonly에서도 코드값(B15135 등)을 표시명으로 바꾸기 위해 Country/State 로드 (Postcode all은 필요 시)
        await loadAltCountryOptions();
        await loadAltStateOptions();
        
        // altAddressData에서 초기값 설정
        console.log('🔍 [ContactInfoForm] ====== loadAltMyOptions 시작 ======');
        console.log('🔍 [ContactInfoForm] altAddressData.value:', altAddressData.value);
        console.log('🔍 [ContactInfoForm] altAddressData.value.localAddress:', altAddressData.value.localAddress);
        
        // Service Address는 alternateAddress 또는 altLocalAddress에서만 가져옴
        // residential_address는 사용하지 않음 (Residential Address 섹션 전용)
        const value = props.modelValue || {} as any;
        
        if (altAddressData.value.localAddress) {
            const localAddr = altAddressData.value.localAddress;
            
            console.log('🔍 [ContactInfoForm] localAddr 전체:', localAddr);
            console.log('🔍 [ContactInfoForm] localAddr.addressLine1:', localAddr.addressLine1);
            console.log('🔍 [ContactInfoForm] localAddr.addressLine2:', localAddr.addressLine2);
            console.log('🔍 [ContactInfoForm] localAddr.addressLine3:', localAddr.addressLine3);
            console.log('🔍 [ContactInfoForm] localAddr.street01:', (localAddr as any).street01);
            console.log('🔍 [ContactInfoForm] localAddr.street02:', (localAddr as any).street02);
            console.log('🔍 [ContactInfoForm] localAddr.street03:', (localAddr as any).street03);
            
            // street01, street02, street03를 addressLine1, addressLine2, addressLine3로 매핑
            const addressLine1 = localAddr.addressLine1 || (localAddr as any).street01 || '';
            const addressLine2 = localAddr.addressLine2 || (localAddr as any).street02 || '';
            const addressLine3 = localAddr.addressLine3 || (localAddr as any).street03 || '';
            
            console.log('🔍 [ContactInfoForm] 매핑된 addressLine 값:', {
                addressLine1,
                addressLine2,
                addressLine3
            });
            
            // readonly일 때는 JSON에서 받은 값 그대로 사용
            if (readonly.value) {
            altMyAddressData.value = {
                addressLine1: addressLine1,
                addressLine2: addressLine2,
                addressLine3: addressLine3,
                country: localAddr.country || '', // JSON의 원본 값
                state: localAddr.state || '', // JSON의 원본 값
                city: localAddr.city || '', // JSON의 원본 값
                postcode: localAddr.postcode || localAddr.postalCode || (localAddr as any).post_code || '', // JSON의 원본 값
            };
            console.log('✅ [ContactInfoForm] Initial MY alt address data set (readonly):', {
                altMyAddressData: altMyAddressData.value,
                altLocalAddress: localAddr
            });
        } else {
            // readonly가 아닐 때는 옵션 로드 및 순차 로직 수행
            // A06002 (Manual)인지 확인
            const registerType = normalizeRegisterType(
                value.CONTACT_REGISTER_TYPE || value.contactRegisterType
            );
            const isManual = registerType === 'A06002';
            
            // 백엔드에서 역추적한 GUID 값을 우선 사용
            // residential_address에 이미 GUID가 포함되어 있으면 사용, 없으면 역추적
            // Entity_my와 동일한 패턴: postcodeCode || postcode (텍스트 값도 확인)
            
            // 🔍 [DEBUG] 원본 데이터 확인
            console.log('🔍 [ContactInfoForm] ====== 원본 데이터에서 GUID 추출 (Service Address만) ======');
            console.log('🔍 [ContactInfoForm] localAddr 전체:', localAddr);
            console.log('🔍 [ContactInfoForm] localAddr.countryCode:', (localAddr as any).countryCode);
            console.log('🔍 [ContactInfoForm] localAddr.stateCode:', (localAddr as any).stateCode);
            console.log('🔍 [ContactInfoForm] localAddr.cityCode:', (localAddr as any).cityCode);
            console.log('🔍 [ContactInfoForm] localAddr.postcodeCode:', (localAddr as any).postcodeCode);
            
            // Service Address만 사용 (residentialAddr 제거)
            let countryCode = (localAddr as any).countryCode || '';
            let stateCode = (localAddr as any).stateCode || '';
            let cityCode = (localAddr as any).cityCode || '';
            // Postcode GUID 값 추출 (역추적이 아닌 저장된 GUID 값 사용)
            let postcodeCode = (localAddr as any).postcodeCode || '';
            
            console.log('🔍 [ContactInfoForm] ====== 추출된 GUID 값 ======');
            console.log('🔍 [ContactInfoForm] countryCode (GUID):', countryCode);
            console.log('🔍 [ContactInfoForm] stateCode (GUID):', stateCode);
            console.log('🔍 [ContactInfoForm] cityCode (GUID):', cityCode);
            console.log('🔍 [ContactInfoForm] postcodeCode (GUID):', postcodeCode);
            
            // GUID 값이 없으면 텍스트 값을 사용 (역추적을 위해)
            const countryName = countryCode ? '' : (localAddr.country || '');
            const stateName = stateCode ? '' : (localAddr.state || '');
            const cityName = cityCode ? '' : (localAddr.city || '');
            const postcodeName = postcodeCode ? '' : (localAddr.postcode || localAddr.postalCode || (localAddr as any).post_code || '');
            
            console.log('🔍 [ContactInfoForm] ====== 텍스트 값 (GUID가 없을 때만 사용) ======');
            console.log('🔍 [ContactInfoForm] countryName:', countryName);
            console.log('🔍 [ContactInfoForm] stateName:', stateName);
            console.log('🔍 [ContactInfoForm] cityName:', cityName);
            console.log('🔍 [ContactInfoForm] postcodeName:', postcodeName);
            
            if (isManual) {
                // A06002일 때: CODE_NAME → TOTAL_CODE 변환
                console.log('🔍 [ContactInfoForm] A06002 detected, converting CODE_NAME to TOTAL_CODE (Alt Address):', {
                    country: localAddr.country,
                    state: localAddr.state,
                    city: localAddr.city,
                    postcode: localAddr.postcode
                });
                
                // GUID 값이 없을 때만 역추적 수행
                // Country: B15 공통코드에서 CODE_NAME으로 TOTAL_CODE 찾기
                if (countryName && !countryCode) {
                    const countryMatch = altCountryOptions.value.find(opt => 
                        opt.CODE_NAME === countryName || opt.CODE_NAME?.toUpperCase() === String(countryName).toUpperCase()
                    );
                    if (countryMatch) {
                        countryCode = countryMatch.TOTAL_CODE;
                        console.log('✅ [ContactInfoForm] Alt Country CODE_NAME → TOTAL_CODE (역추적):', countryName, '→', countryCode);
                    } else {
                        console.warn('⚠️ [ContactInfoForm] Alt Country 역추적 실패:', countryName);
                    }
                }
                
                // State: stateOptions에서 CODE_NAME으로 TOTAL_CODE 찾기
                if (stateName && !stateCode) {
                    const stateMatch = altStateOptions.value.find(opt => 
                        opt.CODE_NAME === stateName || opt.CODE_NAME?.toUpperCase() === String(stateName).toUpperCase()
                    );
                    if (stateMatch) {
                        stateCode = stateMatch.TOTAL_CODE;
                        console.log('✅ [ContactInfoForm] Alt State CODE_NAME → TOTAL_CODE (역추적):', stateName, '→', stateCode);
                        // State가 찾아지면 City 목록 로드
                        await loadAltCitiesByState(stateCode);
                    } else {
                        console.warn('⚠️ [ContactInfoForm] Alt State 역추적 실패:', stateName);
                    }
                }
                
                // City: cityOptions에서 CODE_NAME으로 TOTAL_CODE 찾기
                if (cityName && !cityCode) {
                    const cityMatch = altCityOptions.value.find(opt => 
                        opt.CODE_NAME === cityName || opt.CODE_NAME?.toUpperCase() === String(cityName).toUpperCase()
                    );
                    if (cityMatch) {
                        cityCode = cityMatch.TOTAL_CODE;
                        console.log('✅ [ContactInfoForm] Alt City CODE_NAME → TOTAL_CODE (역추적):', cityName, '→', cityCode);
                        // City가 찾아지면 Postcode 목록 로드
                        await loadAltPostcodeOptions(cityCode, true).catch(() => {});
                    } else {
                        console.warn('⚠️ [ContactInfoForm] Alt City 역추적 실패:', cityName);
                    }
                }
                
                // Postcode: postcodeOptions에서 CODE_NAME으로 TOTAL_CODE 찾기
                // Postcode는 City가 있어야 정확한 역추적이 가능하므로 City가 있을 때만 수행
                if (postcodeName && !postcodeCode && cityCode) {
                    // Postcode 옵션이 로드되지 않았으면 먼저 로드
                    if (altPostcodeOptions.value.length === 0) {
                        await loadAltPostcodeOptions(cityCode, true).catch(() => {});
                    }
                    
                    const postcodeMatch = altPostcodeOptions.value.find(opt => 
                        opt.CODE_NAME === postcodeName || opt.CODE_NAME?.toUpperCase() === String(postcodeName).toUpperCase()
                    );
                    if (postcodeMatch) {
                        postcodeCode = postcodeMatch.TOTAL_CODE;
                        console.log('✅ [ContactInfoForm] Alt Postcode CODE_NAME → TOTAL_CODE (역추적):', postcodeName, '→', postcodeCode);
                    } else {
                        console.warn('⚠️ [ContactInfoForm] Alt Postcode 역추적 실패:', postcodeName, 'cityCode:', cityCode);
                    }
                } else if (postcodeName && !postcodeCode && !cityCode) {
                    console.warn('⚠️ [ContactInfoForm] Alt Postcode 역추적 불가 (City가 없음):', postcodeName);
                }
            }
            
            // Country가 있으면 countryOptions에 추가 (없는 경우)
            if (countryCode) {
                const countryExists = altCountryOptions.value.some(opt => opt.TOTAL_CODE === countryCode);
                if (!countryExists) {
                    altCountryOptions.value.push({
                        TOTAL_CODE: String(countryCode).trim(),
                        CODE_NAME: String(localAddr.country || countryCode).trim().toUpperCase()
                    });
                }
            }
            
            // State가 있으면 City 목록 로드 (Entity_my 패턴)
            if (stateCode && !isManual) {
                await loadAltCitiesByState(stateCode);
            }
            
            // City가 있으면 City 옵션에 추가 및 Postcode 목록 로드 (Entity_my 패턴)
            if (cityCode && cityCode.trim()) {
                const cityExists = altCityOptions.value.some(opt => opt.TOTAL_CODE === cityCode);
                if (!cityExists) {
                    altCityOptions.value.push({
                        TOTAL_CODE: String(cityCode).trim(),
                        CODE_NAME: String(localAddr.city || cityCode).trim()
                    });
                    // City 이름 조회 시도
                    entityMyApi.getMalaysiaCities(undefined, cityCode)
                        .then((cityResponse: any) => {
                            const cityData = cityResponse.data?.data || cityResponse.data;
                            const cityInfo = Array.isArray(cityData) ? cityData[0] : (cityData || {});
                            if (cityInfo?.name) {
                                const cityIndex = altCityOptions.value.findIndex(opt => opt.TOTAL_CODE === cityCode);
                                if (cityIndex >= 0) {
                                    altCityOptions.value[cityIndex].CODE_NAME = String(cityInfo.name).trim();
                                }
                            }
                        })
                        .catch(() => {});
                }
                // City가 있으면 Postcode 목록 로드 (Entity_my 패턴: City 로드 후 Postcode 목록 먼저 로드)
                // A06001과 A06002 모두 Postcode 목록 로드
                await loadAltPostcodeOptions(cityCode, true).catch(() => {
                    console.log('ℹ️ [ContactInfoForm] Postcode 목록 로드 실패');
                });
            }
            
            // Postcode 값 바인딩 (Entity_my 패턴: postcodeCode가 있으면 무조건 옵션에 추가)
            console.log('🔍 [ContactInfoForm] Postcode 바인딩 체크:', {
                postcodeCode,
                postcodeCodeType: typeof postcodeCode,
                postcodeCodeLength: postcodeCode?.length,
                postcodeOptionsBefore: altPostcodeOptions.value.length,
                postcodeName,
                cityCode
            });
            
            // Entity_my 패턴: postcodeCode가 있으면 무조건 옵션에 추가
            if (postcodeCode && postcodeCode.trim()) {
                // Postcode가 옵션에 있는지 확인
                let postcodeExists = altPostcodeOptions.value.some(opt => opt.TOTAL_CODE === postcodeCode);
                console.log('🔍 [ContactInfoForm] Postcode 존재 여부 (첫 확인):', postcodeExists);
                
                // City의 Postcode 목록이 로드되지 않았으면 먼저 로드 시도
                if (!postcodeExists && cityCode) {
                    console.log('🔍 [ContactInfoForm] Postcode 옵션에 없음, City의 Postcode 목록 로드 시도');
                    await loadAltPostcodeOptions(cityCode, true).catch(() => {});
                    // 다시 확인
                    postcodeExists = altPostcodeOptions.value.some(opt => opt.TOTAL_CODE === postcodeCode);
                    console.log('🔍 [ContactInfoForm] Postcode 존재 여부 (로드 후):', postcodeExists);
                }
                
                // 여전히 없으면 수동으로 추가
                if (!postcodeExists) {
                    // Entity_my 패턴: postcodeCode를 CODE_NAME으로도 사용 (postcodeName이 있으면 우선 사용)
                    // postcodeName이 없으면 Postcode 목록에서 찾기 시도
                    let postcodeDisplayName = postcodeName;
                    if (!postcodeDisplayName) {
                        // 전국 캐시에서 찾기(콤보용 altPostcodeOptions는 City 한정 유지)
                        if (altPostcodeAllOptionsCache.value.length === 0) {
                            await loadAllAltPostcodeOptions();
                        }
                        let postcodeMatch = mergedAltPostcodeAutocompleteItems.value.find(
                            (opt) => opt.TOTAL_CODE === postcodeCode
                        );
                        if (postcodeMatch) {
                            postcodeDisplayName = postcodeMatch.CODE_NAME;
                            console.log('✅ [ContactInfoForm] Postcode 옵션에서 이름 찾음:', postcodeDisplayName);
                        } else {
                            // 찾지 못하면 GUID 값 자체를 사용하지 않고 빈 문자열 사용
                            postcodeDisplayName = '';
                            console.warn('⚠️ [ContactInfoForm] Postcode 이름을 찾을 수 없음, 빈 문자열 사용');
                        }
                    }
                    
                    // Postcode 옵션에 추가
                    altPostcodeOptions.value.push({
                        TOTAL_CODE: String(postcodeCode).trim(),
                        CODE_NAME: postcodeDisplayName || String(postcodeCode).trim() // 최후의 수단으로 GUID 사용
                    });
                    console.log('✅ [ContactInfoForm] Alt Postcode 추가됨:', {
                        TOTAL_CODE: postcodeCode,
                        CODE_NAME: postcodeDisplayName || String(postcodeCode).trim(),
                        postcodeOptionsLength: altPostcodeOptions.value.length
                    });
                } else {
                    console.log('ℹ️ [ContactInfoForm] Postcode 이미 존재:', postcodeCode);
                }
            } else {
                console.log('⚠️ [ContactInfoForm] postcodeCode가 없거나 비어있음');
                
                // GUID가 없고 텍스트 값만 있는 경우, Postcode 목록에서 찾기 시도
                if (postcodeName) {
                    // City가 있으면 해당 City의 Postcode 목록에서 찾기, 없으면 전체 목록에서 찾기
                    if (cityCode) {
                        // City의 Postcode 목록이 로드되지 않았으면 로드
                        if (altPostcodeOptions.value.length === 0) {
                            await loadAltPostcodeOptions(cityCode, true).catch(() => {});
                        }
                    } else {
                        // City가 없으면 전국 캐시에서 찾기
                        if (altPostcodeAllOptionsCache.value.length === 0) {
                            await loadAllAltPostcodeOptions();
                        }
                    }
                    
                    const postcodeMatch = mergedAltPostcodeAutocompleteItems.value.find(
                        (opt) =>
                            opt.CODE_NAME === postcodeName ||
                            opt.CODE_NAME?.toUpperCase() === String(postcodeName).toUpperCase()
                    );
                    if (postcodeMatch) {
                        postcodeCode = postcodeMatch.TOTAL_CODE;
                        console.log('✅ [ContactInfoForm] Alt Postcode 목록에서 찾음:', postcodeName, '→', postcodeCode);
                    } else {
                        console.warn('⚠️ [ContactInfoForm] Alt Postcode 역추적 실패:', postcodeName, 'cityCode:', cityCode);
                    }
                }
            }
            
            // Postcode 바인딩 전 최종 확인 및 옵션에 추가 (반드시 바인딩 전에 옵션에 있어야 함)
            if (postcodeCode && postcodeCode.trim()) {
                let finalPostcodeExists = altPostcodeOptions.value.some(opt => opt.TOTAL_CODE === postcodeCode);
                console.log('🔍 [ContactInfoForm] Postcode 바인딩 전 최종 확인:', {
                    postcodeCode,
                    existsInOptions: finalPostcodeExists,
                    optionsLength: altPostcodeOptions.value.length
                });
                
                if (!finalPostcodeExists) {
                    console.warn('⚠️ [ContactInfoForm] Postcode가 옵션에 없음, 바인딩 전 강제 추가');
                    // Postcode 이름 찾기 시도
                    let postcodeDisplayName = postcodeName;
                    if (!postcodeDisplayName) {
                        if (altPostcodeAllOptionsCache.value.length === 0) {
                            await loadAllAltPostcodeOptions();
                        }
                        let postcodeMatch = mergedAltPostcodeAutocompleteItems.value.find(
                            (opt) => opt.TOTAL_CODE === postcodeCode
                        );
                        if (postcodeMatch) {
                            postcodeDisplayName = postcodeMatch.CODE_NAME;
                        }
                    }
                    
                    altPostcodeOptions.value.push({
                        TOTAL_CODE: String(postcodeCode).trim(),
                        CODE_NAME: postcodeDisplayName || String(postcodeCode).trim()
                    });
                    console.log('✅ [ContactInfoForm] Postcode 옵션에 추가 완료 (바인딩 전):', {
                        TOTAL_CODE: postcodeCode,
                        CODE_NAME: postcodeDisplayName || String(postcodeCode).trim()
                    });
                    
                    // 다시 확인
                    finalPostcodeExists = altPostcodeOptions.value.some(opt => opt.TOTAL_CODE === postcodeCode);
                    console.log('🔍 [ContactInfoForm] Postcode 옵션 추가 후 재확인:', finalPostcodeExists);
                }
            }
            
            // 초기 로드 플래그 설정 (watch가 값을 초기화하지 않도록 - Entity_my 패턴)
            // 반드시 바인딩 전에 설정해야 함
            isInitializingAltLocation.value = true;
            
            // countryCode, stateCode, cityCode, postcodeCode를 우선 사용하여 바인딩
            // street01, street02, street03를 addressLine1, addressLine2, addressLine3로 매핑
            // GUID 값이 있으면 GUID 사용, 없으면 빈 문자열 (텍스트 값은 사용하지 않음)
            
            console.log('🔍 [ContactInfoForm] ====== 최종 바인딩 전 GUID 값 확인 ======');
            console.log('🔍 [ContactInfoForm] countryCode (최종):', countryCode, '타입:', typeof countryCode);
            console.log('🔍 [ContactInfoForm] stateCode (최종):', stateCode, '타입:', typeof stateCode);
            console.log('🔍 [ContactInfoForm] cityCode (최종):', cityCode, '타입:', typeof cityCode);
            console.log('🔍 [ContactInfoForm] postcodeCode (최종):', postcodeCode, '타입:', typeof postcodeCode);
            console.log('🔍 [ContactInfoForm] postcodeCode 길이:', postcodeCode?.length);
            console.log('🔍 [ContactInfoForm] postcodeCode가 GUID 형식인가?', postcodeCode && postcodeCode.length > 30);
            console.log('🔍 [ContactInfoForm] postcodeCode가 옵션에 있는가?', postcodeCode ? altPostcodeOptions.value.some(opt => opt.TOTAL_CODE === postcodeCode) : false);
            
            altMyAddressData.value = {
                addressLine1: addressLine1,
                addressLine2: addressLine2,
                addressLine3: addressLine3,
                country: countryCode, // GUID 값만 사용
                state: stateCode, // GUID 값만 사용
                city: cityCode, // GUID 값만 사용
                postcode: postcodeCode, // GUID 값만 사용
            };
            
            console.log('✅ [ContactInfoForm] ====== 최종 altMyAddressData 바인딩 완료 ======');
            console.log('✅ [ContactInfoForm] altMyAddressData.value:', altMyAddressData.value);
            console.log('✅ [ContactInfoForm] country (GUID):', altMyAddressData.value.country);
            console.log('✅ [ContactInfoForm] state (GUID):', altMyAddressData.value.state);
            console.log('✅ [ContactInfoForm] city (GUID):', altMyAddressData.value.city);
            console.log('✅ [ContactInfoForm] postcode (GUID):', altMyAddressData.value.postcode);
            console.log('✅ [ContactInfoForm] postcode가 옵션에 있는가?', altMyAddressData.value.postcode ? altPostcodeOptions.value.some(opt => opt.TOTAL_CODE === altMyAddressData.value.postcode) : false);
            
            // DOM 업데이트 대기
            await nextTick();
            
            // Postcode 바인딩 후 최종 확인
            if (altMyAddressData.value.postcode && altMyAddressData.value.postcode.trim()) {
                const postcodeInOptions = altPostcodeOptions.value.some(opt => opt.TOTAL_CODE === altMyAddressData.value.postcode);
                console.log('🔍 [ContactInfoForm] Postcode 바인딩 후 최종 확인:', {
                    postcode: altMyAddressData.value.postcode,
                    existsInOptions: postcodeInOptions,
                    optionsLength: altPostcodeOptions.value.length,
                    firstFewOptions: altPostcodeOptions.value.slice(0, 3).map(opt => ({ TOTAL_CODE: opt.TOTAL_CODE, CODE_NAME: opt.CODE_NAME }))
                });
                
                if (!postcodeInOptions) {
                    console.error('❌ [ContactInfoForm] Postcode가 옵션에 없음! 강제 추가 시도');
                    // Postcode 이름 찾기 시도
                    let finalPostcodeDisplayName = postcodeName;
                    altPostcodeOptions.value.push({
                        TOTAL_CODE: String(altMyAddressData.value.postcode).trim(),
                        CODE_NAME: finalPostcodeDisplayName || String(altMyAddressData.value.postcode).trim()
                    });
                }
            }
            
            console.log('✅ [ContactInfoForm] Initial MY alt address data set:', {
                altMyAddressData: altMyAddressData.value,
                altLocalAddress: localAddr,
                postcodeOptionsLength: altPostcodeOptions.value.length,
                postcodeInOptions: altMyAddressData.value.postcode ? altPostcodeOptions.value.some(opt => opt.TOTAL_CODE === altMyAddressData.value.postcode) : false,
                isManual
            });
            
            // 초기 로드 완료 후 플래그 해제 (watch가 다시 실행되도록)
            // setTimeout을 사용하여 모든 watch가 완료된 후 해제
            await nextTick();
            setTimeout(() => {
                isInitializingAltLocation.value = false;
                console.log('✅ [ContactInfoForm] isInitializingAltLocation 플래그 해제');
            }, 200); // 200ms로 증가하여 모든 watch가 완료되도록
            }
        }
        syncAltCountrySearchFromModel();
    } finally {
        nextTick(() => {
            isSyncingAltMyAddress.value = false;
        });
    }
};

const contactInfoFormMounted = ref(false);
const altMyAddressBound = ref(false);

onMounted(async () => {
    contactInfoFormMounted.value = true;
    // Country Code 옵션 로드
    loadCountryCodes();
    
    if (isJurisdictionMY.value) {
        if (props.suppressParentEmit) {
            await loadAltCountryOptions();
            await loadAltStateOptions();
        } else if (altAddressData.value?.localAddress) {
            await loadAltMyOptions();
            altMyAddressBound.value = true;
        } else {
            await loadAltCountryOptions();
            await loadAltStateOptions();
        }
    }
});

onBeforeUnmount(() => {
    contactInfoFormMounted.value = false;
});

// isJurisdictionMY가 변경될 때 옵션 로드 (세션이 나중에 로드되는 경우 대비)
watch(isJurisdictionMY, async (newValue, oldValue) => {
    if (newValue && !oldValue) {
        if (props.suppressParentEmit) {
            try {
                await loadAltCountryOptions();
                await loadAltStateOptions();
            } catch (e) {
                console.warn('[ContactInfoForm] loadAltCountry/State failed (create)', e);
            }
            return;
        }
        console.log('🔍 [ContactInfoForm] isJurisdictionMY changed to true, loading alt address options...');
        try {
            await loadAltMyOptions();
        } catch (e) {
            console.warn('[ContactInfoForm] loadAltMyOptions failed', e);
        }
    }
}, { immediate: false });

const normalizeEmailSyncYn = (
    emails: string[],
    sourceSyncYn: Array<boolean | number | string> | undefined,
    lockPrimaryEmail: boolean,
    isManual: boolean
): boolean[] => {
    const normalized: boolean[] = [];
    const hasSource = Array.isArray(sourceSyncYn) && sourceSyncYn.length > 0;

    emails.forEach((_, index) => {
        if (isManual) {
            normalized.push(false);
        } else if (lockPrimaryEmail && index === 0) {
            normalized.push(true);
        } else if (hasSource && index < (sourceSyncYn as Array<boolean | number | string>).length) {
            const raw = (sourceSyncYn as Array<boolean | number | string>)[index];
            normalized.push(raw === true || raw === 1 || raw === '1');
        } else {
            normalized.push(index === 0);
        }
    });

    return normalized;
};

const normalizeContactInfo = (modelValue?: ContactInfoModel | Partial<Contact>): ContactInfoModel => {
    const value = (modelValue ?? {}) as ContactInfoModel & Partial<Contact>;
    const backendValue = (modelValue ?? {}) as Partial<Contact>;
    const registerType = props.manualOverride ? 'A06002' : normalizeRegisterType(
        backendValue.CONTACT_REGISTER_TYPE
        || backendValue.contactRegisterType
        || value.CONTACT_REGISTER_TYPE
        || value.contactRegisterType
    );
    const isManual = registerType === 'A06002';

    let emails: string[] = [];
    if (Array.isArray(value.emails) && value.emails.length > 0) {
        emails = [...value.emails];
    } else if (backendValue.CONTACT_EMAIL) {
        emails = [String(backendValue.CONTACT_EMAIL)];
    }
    if (emails.length === 0) {
        emails = [''];
    }

    let emailMainYn: boolean[] = Array.isArray(value.emailMainYn) ? [...value.emailMainYn] : [];
    if (emailMainYn.length === 0 && emails.length > 0) {
        emailMainYn = [true];
    }
    while (emailMainYn.length < emails.length) {
        emailMainYn.push(false);
    }

    const limitedEmails = emails.slice(0, 2);
    const limitedEmailMainYn = emailMainYn.slice(0, limitedEmails.length);
    const limitedEmailSyncYn = normalizeEmailSyncYn(
        limitedEmails,
        Array.isArray(value.emailSyncYn) ? value.emailSyncYn.slice(0, limitedEmails.length) : undefined,
        props.lockPrimaryEmail,
        isManual
    );

    if (limitedEmails.length > 0 && !limitedEmailMainYn.some(Boolean)) {
        limitedEmailMainYn[0] = true;
    }
    while (limitedEmailMainYn.length < limitedEmails.length) {
        limitedEmailMainYn.push(false);
    }
    while (limitedEmailSyncYn.length < limitedEmails.length) {
        limitedEmailSyncYn.push(false);
    }

    const mobileNos = Array.isArray(value.mobileNos) && value.mobileNos.length > 0
        ? [...value.mobileNos]
        : (backendValue.MOBILE_NO ? [String(backendValue.MOBILE_NO)] : ['']);

    return {
        countryCode: value.countryCode ?? backendValue.COUNTRY_CD ?? '',
        areaCode: isJurisdictionMY.value
            ? ''
            : (value.areaCode ?? backendValue.AREA_CODE ?? ''),
        mobileNos,
        emails,
        emailMainYn: emailMainYn.map((flag, index) => emailMainYn[index] ?? (index === 0)),
        emailSyncYn: normalizeEmailSyncYn(emails, value.emailSyncYn, props.lockPrimaryEmail, isManual),
        note: (value as any)?.note ?? (backendValue as any)?.NOTE ?? '',
        CONTACT_REGISTER_TYPE: registerType,
        contactRegisterType: registerType
    };
};

const buildEmitPayload = (value: ContactInfoModel): ContactInfoModel => {
    const emails = Array.isArray(value.emails) && value.emails.length > 0 ? [...value.emails] : [''];
    const registerType = props.manualOverride ? 'A06002' : normalizeRegisterType(value.CONTACT_REGISTER_TYPE || value.contactRegisterType);
    const isManual = registerType === 'A06002';

    const normalized = normalizeEmailArrays(
        emails,
        value.emailMainYn,
        props.lockPrimaryEmail,
        isManual
    );

    const normalizedSync = normalizeEmailSyncYn(
        emails,
        value.emailSyncYn,
        props.lockPrimaryEmail,
        isManual
    );

    const limitedEmails = emails.slice(0, 2);
    const limitedEmailMainYn = normalized.emailMainYn.slice(0, limitedEmails.length);
    const limitedEmailSyncYn = normalizedSync.slice(0, limitedEmails.length);

    while (limitedEmailMainYn.length < limitedEmails.length) {
        limitedEmailMainYn.push(false);
    }
    while (limitedEmailSyncYn.length < limitedEmails.length) {
        limitedEmailSyncYn.push(false);
    }

    if (limitedEmails.length > 0 && !limitedEmailMainYn.some(Boolean)) {
        limitedEmailMainYn[0] = true;
    }

    const mobileNos = Array.isArray(value.mobileNos) && value.mobileNos.length > 0 ? [...value.mobileNos] : [''];

    // 기존 props.modelValue에서 주소 관련 필드 유지
    const currentValue = getEffectiveModelValue() as Partial<Contact>;
    const backendValue = currentValue as Partial<Contact>;

    // altLocalAddress 객체 가져오기 (altAddressData computed setter에서 설정됨)
    const altLocalAddress = (value as any)?.altLocalAddress || (currentValue as any)?.altLocalAddress;

    const branchAddrLists = useBranchAddresses.value ? buildBranchAddressListsForEmit() : null;
    const serviceAddressList = branchAddrLists
        ? branchAddrLists.serviceAddressList
        : mergeContactJsonAddressArrayForSave(
              serviceAddressRawListFromJson.value,
              isJurisdictionMY.value
                  ? serviceAddressDraftList.value.map((r) => mapMyJsonAddressRowForPersist({ ...r }))
                  : serviceAddressDraftList.value,
              servicePrimarySnapshotForMerge.value
          );
    const businessAddressList = branchAddrLists
        ? branchAddrLists.businessAddressList
        : mergeContactJsonAddressArrayForSave(
              businessAddressRawListFromJson.value,
              isJurisdictionMY.value
                  ? businessAddressDraftList.value.map((r) => mapMyJsonAddressRowForPersist({ ...r }))
                  : businessAddressDraftList.value,
              businessPrimarySnapshotForMerge.value
          );

    const branchContactJsonData =
        branchAddrLists && typeof currentValue.CONTACT_JSON_DATA === 'object' && currentValue.CONTACT_JSON_DATA
            ? {
                  ...(currentValue.CONTACT_JSON_DATA as Record<string, unknown>),
                  service_address: branchAddrLists.serviceAddressList,
                  business_address: branchAddrLists.businessAddressList,
              }
            : branchAddrLists
              ? {
                    service_address: branchAddrLists.serviceAddressList,
                    business_address: branchAddrLists.businessAddressList,
                }
              : undefined;

    return {
        ...currentValue, // 기존 모든 필드 유지 (주소 포함)
        ...value, // contactInfo 필드로 덮어쓰기
        countryCode: value.countryCode ?? null,
        COUNTRY_CD: value.countryCode ?? null,
        areaCode: isJurisdictionMY.value ? '' : (value.areaCode ?? ''),
        AREA_CODE: isJurisdictionMY.value ? '' : (value.areaCode ?? ''),
        mobileNos,
        MOBILE_NO: mobileNos[0] || '',
        emails: limitedEmails,
        emailMainYn: limitedEmailMainYn,
        emailSyncYn: limitedEmailSyncYn,
        CONTACT_EMAIL: limitedEmails[0] || '',
        note: value.note ?? '',
        NOTE: value.note ?? '',
        CONTACT_REGISTER_TYPE: registerType,
        contactRegisterType: registerType,
        // Alternative Address 필드 유지
        ALT_POSTAL_CODE: backendValue.ALT_POSTAL_CODE || (currentValue as any)?.altPostalCode || '',
        ALT_BLOCK_NO: backendValue.ALT_BLOCK_NO || (currentValue as any)?.altBlockNo || '',
        ALT_STREET_NAME: backendValue.ALT_STREET_NAME || (currentValue as any)?.altStreetName || '',
        ALT_BUILDING_NAME: backendValue.ALT_BUILDING_NAME || (currentValue as any)?.altBuildingName || '',
        ALT_ADDR_LEVEL: backendValue.ALT_ADDR_LEVEL || (currentValue as any)?.altAddrLevel || '',
        ALT_ADDR_UNIT: backendValue.ALT_ADDR_UNIT || (currentValue as any)?.altAddrUnit || '',
        ALT_FOREIGN_ADDRESS_LINE_1: backendValue.ALT_FOREIGN_ADDRESS_LINE_1 || (currentValue as any)?.altForeignAddressLine1 || '',
        ALT_FOREIGN_ADDRESS_LINE_2: backendValue.ALT_FOREIGN_ADDRESS_LINE_2 || (currentValue as any)?.altForeignAddressLine2 || '',
        // altLocalAddress 객체 포함 (ContactInfoForm의 altAddressData computed setter에서 설정됨)
        altLocalAddress: altLocalAddress || (currentValue as any)?.altLocalAddress,
        serviceAddressList,
        businessAddressList,
        ...(branchAddrLists
            ? {
                  service_address: branchAddrLists.serviceAddressList,
                  business_address: branchAddrLists.businessAddressList,
                  ...(branchContactJsonData ? { CONTACT_JSON_DATA: branchContactJsonData } : {}),
              }
            : {}),
    };
};

const contactInfo = ref<ContactInfoModel>(normalizeContactInfo(props.modelValue));
let isSyncingFromProps = false;
/** suppressParentEmit: alt·백엔드 필드는 캐시에 보관 (부모 contactInfo 슬라이스가 비어 있어도 Save 가능) */
const suppressedModelCache = ref<Partial<ContactInfoModel>>({});
const hasSuppressPropsInit = ref(false);

function getEffectiveModelValue(): Partial<ContactInfoModel> {
    if (!props.suppressParentEmit) {
        return props.modelValue ?? {};
    }
    return { ...(props.modelValue ?? {}), ...suppressedModelCache.value };
}

const hasBoundCountryCode = ref(false);
const countryCodeSearch = ref('');

watch(
    isJurisdictionMY,
    (isMy) => {
        if (isMy && contactInfo.value.areaCode) {
            contactInfo.value.areaCode = '';
        }
    },
    { immediate: true }
);

watch(
    () => props.modelValue,
    (newValue) => {
        if (isAddressPickerDialogOpen()) return;
        if (props.suppressParentEmit) {
            if (!hasSuppressPropsInit.value) {
                hasSuppressPropsInit.value = true;
                isSyncingFromProps = true;
                contactInfo.value = normalizeContactInfo(newValue);
                suppressedModelCache.value = { ...(newValue ?? {}) };
                nextTick(() => {
                    isSyncingFromProps = false;
                });
            }
            return;
        }
        isSyncingFromProps = true;
        contactInfo.value = normalizeContactInfo(newValue);
        nextTick(() => {
            isSyncingFromProps = false;
        });
    },
    { deep: true }
);

function emitPayloadIfChanged(payload: ContactInfoModel) {
    if (props.suppressParentEmit) return;
    if (isSyncingFromProps) return;
    if (isAddressPickerDialogOpen()) return;
    try {
        if (JSON.stringify(payload) === JSON.stringify(props.modelValue ?? {})) return;
    } catch {
        /* ignore compare failure — emit */
    }
    emit('update:modelValue', payload);
}

watch(
    contactInfo,
    (newValue) => {
        if (props.suppressParentEmit) return;
        if (isSyncingFromProps) return;
        if (isAddressPickerDialogOpen()) return;
        emitPayloadIfChanged(buildEmitPayload(newValue));
    },
    { deep: true }
);

/** 비-branch: draft 변경 시 부모 반영. branch는 편집 중 emit 금지(merge 중복·루프 방지) — Save 시 expose로 flush */
watch(
    [serviceAddressDraftList, businessAddressDraftList],
    () => {
        if (props.suppressParentEmit) return;
        if (isSyncingFromProps) return;
        if (isSyncingAddressDraftsFromProps.value) return;
        if (useBranchAddresses.value) return;
        emitPayloadIfChanged(buildEmitPayload(contactInfo.value));
    },
    { deep: true }
);

defineExpose({
    getContactInfoPayloadForSave: () => buildEmitPayload(contactInfo.value),
    getBranchAddressListsForSave: () =>
        useBranchAddresses.value || props.registerAddressDeleteContext
            ? buildBranchAddressListsForEmit()
            : null,
});

// Alternative Address Postal Code 변경 시 검색 상태 리셋
watch(() => {
    const backendValue = (props.modelValue ?? {}) as Partial<Contact>;
    return backendValue.ALT_POSTAL_CODE || (props.modelValue as any)?.altPostalCode || '';
}, () => {
    isAltPostalCodeSearched.value = false;
    altPostalCodeError.value = null;
});

// Alternative Address Type 변경 시 검색 상태 리셋 (ADDRESS_TYPE_CD 변경 감지)
watch(() => {
    // props.addressType이 있으면 우선 사용, 없으면 props.modelValue에서 읽기
    if (props.addressType) {
        return props.addressType;
    }
    const backendValue = (props.modelValue ?? {}) as Partial<Contact>;
    return backendValue.ADDRESS_TYPE_CD || (props.modelValue as any)?.addressTypeCd || 'B07001';
}, () => {
    isAltPostalCodeSearched.value = false;
    altPostalCodeError.value = null;
});

// Email Validation Rules
const buildEmailRule = (requireValue: boolean) => (v: string) => {
    if (!v) {
        if (requireValue) {
            return (t.value('contact.E-Mail') || 'E-Mail') + ' ' + (t.value('contact.is required') || 'is required');
        }
        return true;
    }
    const result = validateEmail(v, { strict: false, allowEmpty: false, t: t.value });
    return result.isValid || result.message || (t.value('contact.Invalid email format') || 'Invalid email format');
};

const emailRules = computed(() => [buildEmailRule(true)]);
const optionalEmailRules = computed(() => [buildEmailRule(false)]);

const isPrimaryEmailLocked = (index: number) => {
    if (isFromManual.value || props.forceUnlockPrimaryEmail) {
        return false;
    }
    if (!props.lockPrimaryEmail) {
        return false;
    }
    return index === 0;
};

const isPrimaryEmailDisabled = (index: number) => {
    // 첫 번째 이메일이 비어있으면 disabled 해제 (수정 가능)
    if (index === 0 && isFirstEmailEmpty.value) {
        return false;
    }
    if (isFromManual.value || props.forceUnlockPrimaryEmail) {
        return false;
    }
    if (!props.lockPrimaryEmail || index !== 0) {
        return false;
    }
    const email = contactInfo.value.emails?.[index] ?? '';
    return email.trim() === '';
};

const isPrimaryCheckboxDisabled = (index: number) => {
    if (readonly.value) {
        return true;
    }
    if (index !== 0) {
        return false;
    }
    if (isFromManual.value || props.forceUnlockPrimaryEmail) {
        return false;
    }
    const email = contactInfo.value.emails?.[index] ?? '';
    return email.trim() === '';
};

const normalizeEmailArrays = (
    emails: string[],
    emailMainYn?: Array<boolean | number | string>,
    lockPrimaryEmail?: boolean,
    isManual?: boolean
) => {
    const normalizedMainYn = (emailMainYn || [])
        .slice(0, emails.length)
        .map((value) => value === true || value === 1 || value === '1');
    while (normalizedMainYn.length < emails.length) {
        normalizedMainYn.push(false);
    }
    if (emails.length > 0 && !normalizedMainYn.some((value) => value === true)) {
        normalizedMainYn[0] = true;
    }

    const normalizedSyncYn = normalizeEmailSyncYn(
        emails,
        undefined,
        !!lockPrimaryEmail,
        !!isManual
    );

    return {
        emails,
        emailMainYn: normalizedMainYn,
        emailSyncYn: normalizedSyncYn
    };
};

const addEmail = () => {
    // 최대 2개까지만 추가 가능
    if (contactInfo.value.emails.length < 2) {
        const current = contactInfo.value;
        const newEmails = [...current.emails, ''];
        const { emailMainYn, emailSyncYn } = normalizeEmailArrays(
            newEmails,
            current.emailMainYn,
            props.lockPrimaryEmail,
            isFromManual.value
        );

        contactInfo.value = {
            ...current,
            emails: newEmails,
            emailMainYn,
            emailSyncYn
        };
    }
};

const removeEmail = (index: number) => {
    if (contactInfo.value.emails.length > 1) {
        const current = contactInfo.value;
        const newEmails = current.emails.filter((_, i) => i !== index);
        const mainArray = (current.emailMainYn || []).filter((_, i) => i !== index);

        // ensure at least one main flag remains true when emails exist
        let adjustedMainArray = [...mainArray];
        if (newEmails.length > 0 && !adjustedMainArray.some(Boolean)) {
            adjustedMainArray[0] = true;
        }

        const { emailMainYn, emailSyncYn } = normalizeEmailArrays(
            newEmails,
            adjustedMainArray,
            props.lockPrimaryEmail,
            isFromManual.value
        );

        contactInfo.value = {
            ...current,
            emails: newEmails,
            emailMainYn,
            emailSyncYn
        };
    }
};

// MAIN_YN 체크박스 변경 핸들러
const updateEmailMainYn = (index: number, value: boolean) => {
    const current = contactInfo.value;
    const total = current.emails.length;
    const baseMainYn =
        current.emailMainYn && current.emailMainYn.length === total
            ? [...current.emailMainYn]
            : current.emails.map((_, i) => i === 0);

    let updatedMainYn: boolean[];
    if (value) {
        updatedMainYn = baseMainYn.map((_, i) => i === index);
    } else {
        updatedMainYn = baseMainYn.map((yn, i) => (i === index ? false : yn));
        if (total > 0 && !updatedMainYn.some(Boolean)) {
            updatedMainYn[index] = true;
        }
    }

    const normalized = normalizeEmailArrays(
        current.emails,
        updatedMainYn,
        props.lockPrimaryEmail,
        isFromManual.value
    );

    contactInfo.value = {
        ...current,
        emailMainYn: normalized.emailMainYn,
        emailSyncYn: normalizeEmailSyncYn(
            current.emails,
            current.emailSyncYn,
            props.lockPrimaryEmail,
            isFromManual.value
        )
    };
};

const addMobile = () => {
    contactInfo.value = {
        ...contactInfo.value,
        mobileNos: [...contactInfo.value.mobileNos, '']
    };
};

const removeMobile = (index: number) => {
    if (contactInfo.value.mobileNos.length > 1) {
        contactInfo.value = {
            ...contactInfo.value,
            mobileNos: contactInfo.value.mobileNos.filter((_, i) => i !== index)
        };
    }
};

// Country Code 목록 (B05 메인코드)
const countryCodeOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string; BIGO3?: string }>>([]);

// Country Code 로드 (B05, CM) - Entity Detail과 동일하게 변경
const loadCountryCodes = async () => {
    try {
        const response = await baseCodeApi.getCodesByMainCode('CM', 'B05', undefined);
        const codes = (response as any).codes || response.data?.codes || [];
        
        // DEFAULT_CODE_NAME 우선 사용, 없으면 CODE_NAME 사용
        // BIGO3도 포함하여 검색 가능하도록
        countryCodeOptions.value = codes
            .map((code: any) => ({
                TOTAL_CODE: String(code.totalCode || code.TOTAL_CODE || '').trim(),
                CODE_NAME: String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim(),
                BIGO3: String(code.bigo3 || code.BIGO3 || '').trim() // BIGO3 추가
            }))
            .filter((item: any) => {
                return item.TOTAL_CODE && item.CODE_NAME && 
                       item.TOTAL_CODE.length > 0 && item.CODE_NAME.length > 0;
            });
        
        // TOTAL_CODE 기준으로 중복 제거
        const uniqueCodesMap = new Map();
        countryCodeOptions.value.forEach((item: any) => {
            if (!uniqueCodesMap.has(item.TOTAL_CODE)) {
                uniqueCodesMap.set(item.TOTAL_CODE, item);
            }
        });
        
        const allOptions = Array.from(uniqueCodesMap.values());
        
        // SUB_CODE가 "000"인 항목 필터링 (Entity의 Jurisdiction 패턴)
        const filteredOptions = allOptions.filter((item: any) => {
            const subCode = item.TOTAL_CODE.slice(-3);
            return subCode !== '000';  // "Please Select" 항목 제거
        });
        
        // "Please Select" 항목을 옵션 목록 맨 앞에 추가 (빈 값으로)
        countryCodeOptions.value = [
            { TOTAL_CODE: '', CODE_NAME: t.value('contact.Please Select') || 'Please Select', BIGO3: '' },
            ...filteredOptions
        ];
        
        // Detail 페이지에서 현재 선택된 COUNTRY_CD가 옵션 목록에 있는지 확인하고 TOTAL_CODE로 바인딩
        await bindCountryCodeAfterOptionsLoaded();
    } catch (error) {
        console.error('❌ [loadCountryCodes] Country Code 로드 실패:', error);
        countryCodeOptions.value = [];
    }
};

// 옵션 로드 완료 후 데이터 바인딩을 위한 함수 (Entity Detail의 jurisdiction 바인딩 방식 참고)
const bindCountryCodeAfterOptionsLoaded = async () => {
    if (hasBoundCountryCode.value) {
        return;
    }
    if (isAddressPickerDialogOpen()) {
        return;
    }

    const currentValue = props.modelValue || {} as any;
    const countryCd = currentValue.countryCode || currentValue.COUNTRY_CD || '';
    
    // null, undefined, 빈 문자열이면 스킵
    if (!countryCd || (typeof countryCd === 'string' && countryCd.trim() === '')) {
        hasBoundCountryCode.value = true;
        return;
    }
    
    const countryCdStr = String(countryCd).trim();
    
    // options에서 TOTAL_CODE로 존재하는지 확인
    const existsAsTotalCode = countryCodeOptions.value.some((opt: any) => opt.TOTAL_CODE === countryCdStr);
    
    if (!existsAsTotalCode && countryCodeOptions.value.length > 0) {
        // TOTAL_CODE가 아니면 CODE_NAME으로 찾아서 TOTAL_CODE로 변환
        const matchedOption = countryCodeOptions.value.find((opt: any) => opt.CODE_NAME === countryCdStr);
        if (matchedOption && contactInfo.value.countryCode !== matchedOption.TOTAL_CODE) {
            await nextTick();
            isSyncingFromProps = true;
            contactInfo.value = {
                ...contactInfo.value,
                countryCode: matchedOption.TOTAL_CODE
            };
            nextTick(() => {
                isSyncingFromProps = false;
                hasBoundCountryCode.value = true;
            });
            console.log('✅ [bindCountryCodeAfterOptionsLoaded] Country Code bound:', matchedOption.TOTAL_CODE);
        } else {
            hasBoundCountryCode.value = true;
        }
    } else if (existsAsTotalCode) {
        // 이미 TOTAL_CODE이면 그대로 사용
        hasBoundCountryCode.value = true;
        console.log('✅ [bindCountryCodeAfterOptionsLoaded] Country Code already TOTAL_CODE:', countryCdStr);
    } else {
        hasBoundCountryCode.value = true;
    }
};

// 현재 선택된 COUNTRY_CD가 옵션 목록에 있는지 확인하고 없으면 추가 (하위 호환성 유지)
const ensureCurrentCountryCodeInOptions = async () => {
    await bindCountryCodeAfterOptionsLoaded();
};

// props.modelValue가 변경될 때 (Detail 페이지에서 데이터 로드 후) 바인딩 플래그 리셋
watch(() => props.modelValue, (newValue) => {
    if (isAddressPickerDialogOpen()) return;
    if (newValue) {
        hasBoundCountryCode.value = false;
        if (countryCodeOptions.value.length > 0) {
            bindCountryCodeAfterOptionsLoaded();
        }
    }
}, { deep: true });

// 옵션이 로드되면 자동으로 값 바인딩 시도
watch(countryCodeOptions, async () => {
    if (isAddressPickerDialogOpen()) return;
    if (props.modelValue && countryCodeOptions.value.length > 0 && !hasBoundCountryCode.value) {
        await bindCountryCodeAfterOptionsLoaded();
    }
}, { deep: true });

// Country Code 변경 핸들러 (Entity의 Jurisdiction 패턴)
const updateCountryCode = (val: any) => {
    if (isAddressPickerDialogOpen()) return;
    const current = contactInfo.value;
    // 빈 문자열이나 null 처리 (Entity의 Jurisdiction처럼)
    const countryCodeValue = (val === '' || val === null || val === undefined) ? null : val;
    const updated = {
        ...current,
        countryCode: countryCodeValue
    };
    console.log('🔄 [updateCountryCode] 업데이트 전:', current.countryCode);
    console.log('🔄 [updateCountryCode] 업데이트 후:', updated.countryCode);
    contactInfo.value = updated;
    console.log('✅ [updateCountryCode] contactInfo.value 업데이트 완료');
};

// Country Code 검색어 업데이트 핸들러 (공백 제거)
const updateCountryCodeSearch = (val: string) => {
    // 앞뒤 공백 제거
    countryCodeSearch.value = val ? val.trim() : '';
};

// Mobile No 레이블 생성 함수
const getMobileLabel = (index: number) => {
    // 점 없는 키를 사용하여 번역 (모든 언어 파일에 존재)
    const mobileNoText = t.value('contact.Mobile No') || 'Mobile No.';
    // 첫 번째 Mobile No는 번호 없이, 나머지는 번호 포함
    return index === 0 ? mobileNoText : `${mobileNoText} ${index + 1}`;
};

// E-Mail 레이블 생성 함수
const getEmailLabel = (index: number) => {
    const emailText = t.value('contact.E-Mail') || 'E-Mail';
    return `${emailText} ${index + 1}`;
};

// From Manual 여부 확인 (CONTACT_REGISTER_TYPE이 'A06002'인 경우)
const isFromManual = computed(() => {
    if (props.manualOverride) {
        return true;
    }
    const backendValue = (props.modelValue ?? {}) as Partial<Contact>;
    const contactRegisterType = normalizeRegisterType(
        backendValue.CONTACT_REGISTER_TYPE || backendValue.contactRegisterType
    );
    return contactRegisterType === 'A06002';
});

// Alternative Address 데이터 - 기존 Residential Address의 ADDRESS_TYPE_CD를 사용
const altAddressData = computed({
    get: () => {
        const value = getEffectiveModelValue();
        const backendValue = value as Partial<Contact>;

        const addressType = (props.addressType || backendValue.ADDRESS_TYPE_CD || (value as any).addressTypeCd || 'B07001') as 'B07001' | 'B07002';

        let alternateAddr: any = null;
        if ((value as any).altLocalAddress) {
            alternateAddr = (value as any).altLocalAddress;
        }

        const addressLine1 = (backendValue as any).ALT_ADDRESS_LINE_1 || (value as any).altAddressLine1 || 
                             ((value as any).altLocalAddress?.addressLine1) || ((value as any).altLocalAddress?.street01) ||
                             (alternateAddr?.street01) || (alternateAddr?.addressLine1) || '';
        const addressLine2 = (backendValue as any).ALT_ADDRESS_LINE_2 || (value as any).altAddressLine2 || 
                             ((value as any).altLocalAddress?.addressLine2) || ((value as any).altLocalAddress?.street02) ||
                             (alternateAddr?.street02) || (alternateAddr?.addressLine2) || '';
        const addressLine3 = (backendValue as any).ALT_ADDRESS_LINE_3 || (value as any).altAddressLine3 || 
                             ((value as any).altLocalAddress?.addressLine3) || ((value as any).altLocalAddress?.street03) ||
                             (alternateAddr?.street03) || (alternateAddr?.addressLine3) || '';

        const localAddressResult = {
                postalCode: backendValue.ALT_POSTAL_CODE || (value as any).altPostalCode || '',
                blockHouseNo: backendValue.ALT_BLOCK_NO || (value as any).altBlockNo || '',
                streetName: backendValue.ALT_STREET_NAME || (value as any).altStreetName || '',
                buildingEstateName: backendValue.ALT_BUILDING_NAME || (value as any).altBuildingName || '',
                level: backendValue.ALT_ADDR_LEVEL || (value as any).altAddrLevel || '',
                unit: backendValue.ALT_ADDR_UNIT || (value as any).altAddrUnit || '',
                // MY인 경우 Entity 스타일 주소 필드
                // street01, street02, street03를 addressLine1, addressLine2, addressLine3로 매핑
                // altLocalAddress도 확인 (ContactDetailPage에서 이미 매핑된 경우)
                addressLine1: addressLine1,
                addressLine2: addressLine2,
                addressLine3: addressLine3,
                country: (backendValue as any).ALT_COUNTRY || (value as any).altCountry || 
                        ((value as any).altLocalAddress?.country) ||
                        (alternateAddr?.country) || '', // residentialAddr 제거 - Service Address만 사용
                state: (backendValue as any).ALT_STATE || (value as any).altState || 
                       ((value as any).altLocalAddress?.state) ||
                       (alternateAddr?.state) || '', // residentialAddr 제거 - Service Address만 사용
                city: (backendValue as any).ALT_CITY || (value as any).altCity || 
                      ((value as any).altLocalAddress?.city) ||
                      (alternateAddr?.city) || '', // residentialAddr 제거 - Service Address만 사용
                postcode: (backendValue as any).ALT_POSTCODE || (value as any).altPostcode || 
                         ((value as any).altLocalAddress?.postcode) || ((value as any).altLocalAddress?.post_code) ||
                         (alternateAddr?.postcode) || (alternateAddr?.post_code) || '', // residentialAddr 제거 - Service Address만 사용
                // MY인 경우 바인딩용 CODE (countryCode, stateCode 등)
                countryCode: (backendValue as any).ALT_COUNTRY_CODE || (value as any).altCountryCode || 
                            (alternateAddr?.countryCode) || // residentialAddr 제거 - Service Address만 사용
                            (backendValue as any).ALT_COUNTRY || (value as any).altCountry || 
                            (alternateAddr?.country) || '', // residentialAddr 제거 - Service Address만 사용
                stateCode: (backendValue as any).ALT_STATE_CODE || (value as any).altStateCode || 
                          (alternateAddr?.stateCode) || // residentialAddr 제거 - Service Address만 사용
                          (backendValue as any).ALT_STATE || (value as any).altState || 
                          (alternateAddr?.state) || '', // residentialAddr 제거 - Service Address만 사용
                cityCode: (backendValue as any).ALT_CITY_CODE || (value as any).altCityCode || 
                         (alternateAddr?.cityCode) || // residentialAddr 제거 - Service Address만 사용
                         (backendValue as any).ALT_CITY || (value as any).altCity || 
                         (alternateAddr?.city) || '', // residentialAddr 제거 - Service Address만 사용
                postcodeCode: (backendValue as any).ALT_POSTCODE_CODE || (value as any).altPostcodeCode || 
                             (alternateAddr?.postcodeCode) || // residentialAddr 제거 - Service Address만 사용
                             (backendValue as any).ALT_POSTCODE || (value as any).altPostcode || 
                             (alternateAddr?.postcode) || (alternateAddr?.post_code) || '', // residentialAddr 제거 - Service Address만 사용
            };

        return {
            addressType,
            localAddress: localAddressResult,
            foreignAddress: {
                addressLine1: backendValue.ALT_FOREIGN_ADDRESS_LINE_1 || (value as any).altForeignAddressLine1 || '',
                addressLine2: backendValue.ALT_FOREIGN_ADDRESS_LINE_2 || (value as any).altForeignAddressLine2 || '',
            },
        };
    },
    set: (value) => {
        if (isAddressPickerDialogOpen()) return;

        const currentValue = getEffectiveModelValue();
        
        const findAltPostcodeName = (postcodeCode: string) => {
            if (!postcodeCode) return '';
            const postcode = mergedAltPostcodeAutocompleteItems.value.find(
                (opt) => opt.TOTAL_CODE === postcodeCode
            );
            return postcode?.CODE_NAME || '';
        };
        
        // MY인 경우 CODE를 CODE_NAME으로 변환
        const localAddr = value.localAddress || {};
        const countryCode = (localAddr as any)?.countryCode || localAddr?.country || '';
        const stateCode = (localAddr as any)?.stateCode || localAddr?.state || '';
        const cityCode = (localAddr as any)?.cityCode || localAddr?.city || '';
        const postcodeCode = (localAddr as any)?.postcodeCode || localAddr?.postcode || '';
        
        const countryStored =
            normalizeMyLocationFieldForPersist(countryCode || localAddr?.country, altCountryOptions.value) ||
            String(countryCode || localAddr?.country || '').trim();
        const stateStored =
            normalizeMyLocationFieldForPersist(stateCode || localAddr?.state, altStateOptions.value) ||
            String(stateCode || localAddr?.state || '').trim();
        const cityStored =
            normalizeMyLocationFieldForPersist(cityCode || localAddr?.city, altCityOptions.value) ||
            String(cityCode || localAddr?.city || '').trim();
        const postcodeName = findAltPostcodeName(postcodeCode) || localAddr?.postcode || '';
        
        // altLocalAddress 객체 생성 (백엔드에서 service_address로 저장하기 위해)
        const altLocalAddressObj: any = {
            postalCode: localAddr?.postalCode || '',
            blockHouseNo: localAddr?.blockHouseNo || '',
            streetName: localAddr?.streetName || '',
            buildingEstateName: localAddr?.buildingEstateName || '',
            level: localAddr?.level || '',
            unit: localAddr?.unit || '',
        };
        
        // MY인 경우 Entity 스타일 주소 필드 (country/state/city는 TOTAL_CODE)
        if (isJurisdictionMY.value) {
            altLocalAddressObj.addressLine1 = localAddr?.addressLine1 || '';
            altLocalAddressObj.addressLine2 = localAddr?.addressLine2 || '';
            altLocalAddressObj.addressLine3 = localAddr?.addressLine3 || '';
            altLocalAddressObj.country = countryStored;
            altLocalAddressObj.state = stateStored;
            altLocalAddressObj.city = cityStored;
            altLocalAddressObj.postcode = postcodeName;
            altLocalAddressObj.post_code = postcodeName;
            altLocalAddressObj.countryCode = countryStored;
            altLocalAddressObj.stateCode = stateStored;
            altLocalAddressObj.cityCode = cityStored;
            altLocalAddressObj.postcodeCode = postcodeCode;
        }
        
        const altPayload = {
            ...currentValue,
            // ALT_ADDRESS_TYPE_CD는 저장하지 않음 (ADDRESS_TYPE_CD를 그대로 사용)
            ALT_POSTAL_CODE: localAddr?.postalCode || '',
            ALT_BLOCK_NO: localAddr?.blockHouseNo || '',
            ALT_STREET_NAME: localAddr?.streetName || '',
            ALT_BUILDING_NAME: localAddr?.buildingEstateName || '',
            ALT_ADDR_LEVEL: localAddr?.level || '',
            ALT_ADDR_UNIT: localAddr?.unit || '',
            ALT_FOREIGN_ADDRESS_LINE_1: value.foreignAddress?.addressLine1 || '',
            ALT_FOREIGN_ADDRESS_LINE_2: value.foreignAddress?.addressLine2 || '',
            // MY인 경우 Entity 스타일 주소 필드 (CODE_NAME으로 저장)
            ALT_ADDRESS_LINE_1: localAddr?.addressLine1 || '',
            ALT_ADDRESS_LINE_2: localAddr?.addressLine2 || '',
            ALT_ADDRESS_LINE_3: localAddr?.addressLine3 || '',
            ALT_COUNTRY: countryStored,
            ALT_STATE: stateStored,
            ALT_CITY: cityStored,
            ALT_POSTCODE: postcodeName,
            ALT_COUNTRY_CODE: countryStored,
            ALT_STATE_CODE: stateStored,
            ALT_CITY_CODE: cityStored,
            ALT_POSTCODE_CODE: postcodeCode,
            // altLocalAddress 객체도 함께 전달 (백엔드에서 service_address로 저장하기 위해)
            altLocalAddress: altLocalAddressObj,
        } as any;
        if (props.suppressParentEmit) {
            suppressedModelCache.value = { ...suppressedModelCache.value, ...altPayload };
            return;
        }
        emit('update:modelValue', altPayload);
    }
});

// Alternative Address용 로딩 상태
const isLoadingAltCities = ref(false);
const isLoadingAltPostcodes = ref(false);
const isInitializingAltLocation = ref(false);
const suppressAltLocationCascade = ref(false);

// Alternative Address용 동기화 플래그 (무한 루프 방지)
const isSyncingAltMyAddress = ref(false);

// Alternative Address용 Country 목록 로드 (세션 캐시)
const loadAltCountryOptions = async () => {
  if (altCountryOptions.value.length > 0) return;
  try {
    const { countries } = await ensureCachedMyCountryAndState();
    altCountryOptions.value = countries;
  } catch (error) {
    console.error('❌ [loadAltCountryOptions] Country 로드 실패:', error);
    altCountryOptions.value = [];
  }
};

// Alternative Address용 State 옵션 로드 (세션 캐시)
const loadAltStateOptions = async () => {
  if (altStateOptions.value.length > 0) return;
  try {
    const { states } = await ensureCachedMyCountryAndState();
    altStateOptions.value = states;
  } catch (error) {
    console.error('❌ [loadAltStateOptions] State 로드 실패:', error);
    altStateOptions.value = [];
  }
};

// Alternative Address용 State별 City 목록 로드
let altCitiesLoadSeq = 0;
const altCityOptionsForStateCode = ref('');
const loadAltCitiesByState = async (stateCode: string | null) => {
  if (!stateCode) {
    altCityOptions.value = [];
    altCityOptionsForStateCode.value = '';
    return;
  }

  const resolvedState =
    resolveTotalCodeFromField(stateCode, altStateOptions.value) || String(stateCode).trim();
  if (!resolvedState) {
    altCityOptions.value = [];
    altCityOptionsForStateCode.value = '';
    return;
  }

  const seq = ++altCitiesLoadSeq;
  isLoadingAltCities.value = true;
  try {
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('City 목록 로드 타임아웃')), 5000)
    );
    
    const response = await Promise.race([
      entityMyApi.getMalaysiaCities(resolvedState, undefined, false),
      timeoutPromise
    ]) as any;

    if (seq !== altCitiesLoadSeq) return;
    
    const cities = response.data?.data || response.data || [];
    
    altCityOptions.value = cities.map((city: any) => ({
      TOTAL_CODE: String(city.code || '').trim(),
      CODE_NAME: String(city.name || '').trim()
    })).filter((item: any) => item.TOTAL_CODE && item.CODE_NAME);
    altCityOptionsForStateCode.value = resolvedState.toLowerCase();
  } catch (error) {
    if (seq !== altCitiesLoadSeq) return;
    console.error('❌ [loadAltCitiesByState] City 목록 로드 실패:', error);
    altCityOptions.value = [];
    altCityOptionsForStateCode.value = '';
  } finally {
    if (seq === altCitiesLoadSeq) {
      isLoadingAltCities.value = false;
    }
  }
};

async function ensureAltCitiesLoadedForState(stateRaw: unknown) {
  const state = String(stateRaw ?? '').trim();
  if (!state) return;
  const resolved =
    resolveTotalCodeFromField(state, altStateOptions.value) || state;
  const resolvedKey = resolved.toLowerCase();
  if (
    altCityOptions.value.length > 0 &&
    altCityOptionsForStateCode.value &&
    altCityOptionsForStateCode.value === resolvedKey
  ) {
    return;
  }
  await loadAltCitiesByState(state);
}

// Alternative Address용 모든 Postcode 목록 로드 (세션 캐시)
const loadAllAltPostcodeOptions = async () => {
  if (isLoadingAltPostcodes.value) {
    return;
  }
  if (altPostcodeAllOptionsCache.value.length > 0) {
    return;
  }

  isLoadingAltPostcodes.value = true;
  try {
    altPostcodeAllOptionsCache.value = await fetchCachedMyAllPostcodeOptions();
    mergeMalaysiaPostcodeApiRowsIntoLookup(getCachedMyAllPostcodeRawRows());
  } catch (error) {
    console.error('❌ [loadAllAltPostcodeOptions] 전체 Postcode 로드 실패:', error);
    altPostcodeAllOptionsCache.value = [];
  } finally {
    isLoadingAltPostcodes.value = false;
  }
};

// Alternative Address용 Postcode 옵션 로드 (City 선택 시)
const loadAltPostcodeOptions = async (cityCode: string | null, preservePostcodeValue: boolean = false) => {
  if (!cityCode) {
    if (altPostcodeAllOptionsCache.value.length === 0) {
      await loadAllAltPostcodeOptions();
    }
    if (!preservePostcodeValue) {
      altMyAddressData.value.postcode = '';
    }
    return;
  }
  
  isLoadingAltPostcodes.value = true;
  try {
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Postcode 목록 로드 타임아웃')), 5000)
    );
    
    const response = await Promise.race([
      entityMyApi.getMalaysiaPostcodes(cityCode),
      timeoutPromise
    ]) as any;
    
    const postcodes = response.data?.data || response.data || [];
    
    // CODE_NAME(우편번호 숫자) 기준으로 중복 제거
    const uniquePostcodesMap = new Map<string, { TOTAL_CODE: string; CODE_NAME: string }>();
    
    postcodes.forEach((postcode: any) => {
      const codeName = String(postcode.name || '').trim();
      const totalCode = String(postcode.code || postcode.name || '').trim();
      
      if (codeName && totalCode) {
        // CODE_NAME을 키로 사용하여 중복 제거
        if (!uniquePostcodesMap.has(codeName)) {
          uniquePostcodesMap.set(codeName, {
            TOTAL_CODE: totalCode,
            CODE_NAME: codeName
          });
        }
      }
    });

    mergeMalaysiaPostcodeApiRowsIntoLookup(postcodes);
    altPostcodeOptions.value = Array.from(uniquePostcodesMap.values());
  } catch (error) {
    console.error('❌ [loadAltPostcodeOptions] Postcode 로드 실패:', error);
    altPostcodeOptions.value = [];
  } finally {
    isLoadingAltPostcodes.value = false;
  }
};

/** Postcode 콤보 열 때 City 없이 역검색하려면 전국 캐시 필요 */
async function ensureAltPostcodeFullCacheForReverse() {
    if (altPostcodeAllOptionsCache.value.length > 0) return;
    await loadAllAltPostcodeOptions();
}

// Alternative Address용 우편번호로 State와 City 찾기 (이미 선택된 State/City 정보 활용)
const findAltStateAndCityByPostcode = async (
  postcodeNumber: string,
  currentStateCode?: string | null,
  currentCityCode?: string | null
) => {
  if (!postcodeNumber || postcodeNumber.trim().length === 0) {
    return null;
  }
  
  try {
    const response = await entityMyApi.getMalaysiaPostcodesByNumber(postcodeNumber.trim());
    const postcodes = (response as any).data?.data || (response as any).data || [];
    
    if (!Array.isArray(postcodes) || postcodes.length === 0) {
      return null;
    }
    
    // 이미 선택된 City가 있으면 해당 City와 일치하는 결과 우선 선택
    if (currentCityCode && postcodes.length > 1) {
      const matchedByCity = postcodes.find((p: any) => p.city?.code === currentCityCode);
      if (matchedByCity) {
        return {
          stateCode: matchedByCity?.state?.code || null,
          cityCode: matchedByCity?.city?.code || null,
          cityName: matchedByCity?.city?.name || null
        };
      }
    }
    
    // City가 없고 State만 선택되어 있으면 해당 State의 결과 우선 선택
    if (currentStateCode && !currentCityCode && postcodes.length > 1) {
      const matchedByState = postcodes.find((p: any) => p.state?.code === currentStateCode);
      if (matchedByState) {
        return {
          stateCode: matchedByState?.state?.code || null,
          cityCode: matchedByState?.city?.code || null,
          cityName: matchedByState?.city?.name || null
        };
      }
    }
    
    // 매칭되는 것이 없거나 하나만 있으면 첫 번째 결과 사용
    const firstPostcode = postcodes[0] as any;
    
    return {
      stateCode: firstPostcode?.state?.code || null,
      cityCode: firstPostcode?.city?.code || null,
      cityName: firstPostcode?.city?.name || null
    };
  } catch (error) {
    console.error('❌ [findAltStateAndCityByPostcode] State/City 찾기 실패:', error);
    return null;
  }
};

// Alternative Address용 State 선택 시 City 목록 로드
watch(() => altMyAddressData.value.state, async (newState, oldState) => {
  if (isInitializingAltLocation.value || suppressAltLocationCascade.value) {
    return;
  }

  // 초기 바인딩: City만 로드 (기존 city/postcode 유지)
  if (oldState === undefined && newState) {
    await loadAltCitiesByState(newState);
    return;
  }
  
  if (newState !== oldState) {
    // State가 입력되면 말레이시아 주소로 간주하여 country를 "B15135"로 자동 바인딩
    if (newState && altMyAddressData.value.country !== 'B15135') {
      altMyAddressData.value.country = 'B15135';
    }
    
    altMyAddressData.value.city = '';
    altMyAddressData.value.postcode = '';
    altPostcodeOptions.value = [];
    
    if (newState) {
      await loadAltCitiesByState(newState);
    } else {
      // State가 지워지면 City 옵션 초기화 — 전국 Postcode는 캐시에만(콤보 altPostcodeOptions는 비움)
      altCityOptions.value = [];
      await loadAllAltPostcodeOptions();
    }
  }
});

// Alternative Address용 City 선택 시 Postcode 목록 로드
watch(() => altMyAddressData.value.city, async (newCity, oldCity) => {
  if (isInitializingAltLocation.value || suppressAltLocationCascade.value) {
    return;
  }
  
  // 초기 로드 시 oldCity가 undefined이고 newCity가 있는 경우는 스킵
  if (oldCity === undefined && newCity) {
    return;
  }
  
  if (newCity !== oldCity) {
    // City가 입력되면 말레이시아 주소로 간주하여 country를 "B15135"로 자동 바인딩
    if (newCity && altMyAddressData.value.country !== 'B15135') {
      altMyAddressData.value.country = 'B15135';
    }
    
    altMyAddressData.value.postcode = '';
    altPostcodeOptions.value = [];
    
    if (newCity) {
      await loadAltPostcodeOptions(newCity, false);
    } else {
      if (altPostcodeAllOptionsCache.value.length === 0) {
        await loadAllAltPostcodeOptions();
      }
    }
  }
});

// Alternative Address용 Postcode 선택 시 State와 City 자동 선택 (역방향 조회)
watch(() => altMyAddressData.value.postcode, async (newPostcode, oldPostcode) => {
  if (isInitializingAltLocation.value || suppressAltLocationCascade.value) {
    return;
  }
  
  // Postcode가 입력되었을 때만 역방향 조회 수행 (지워지면 State/City 유지)
  if (newPostcode !== oldPostcode && newPostcode) {
    // Postcode가 입력되면 말레이시아 주소로 간주하여 country를 "B15135"로 자동 바인딩
    if (newPostcode && altMyAddressData.value.country !== 'B15135') {
      altMyAddressData.value.country = 'B15135';
    }
    
    // 역방향 조회용 전국 캐시
    if (altPostcodeAllOptionsCache.value.length === 0) {
      await loadAllAltPostcodeOptions();
    }
    
    const postcodeNumber =
      mergedAltPostcodeAutocompleteItems.value.find(
        (opt) => opt.TOTAL_CODE === newPostcode || opt.CODE_NAME === newPostcode,
      )?.CODE_NAME ||
      (/^[0-9a-f-]{36}$/i.test(String(newPostcode)) ? '' : String(newPostcode));
    if (!postcodeNumber) return;
    
    const locationInfo = await findAltStateAndCityByPostcode(
      postcodeNumber,
      altMyAddressData.value.state || null,
      altMyAddressData.value.city || null
    );
    
    if (locationInfo && locationInfo.stateCode && locationInfo.cityCode) {
      suppressAltLocationCascade.value = true;
      isInitializingAltLocation.value = true;
      
      try {
        altMyAddressData.value.state = locationInfo.stateCode;
        await loadAltCitiesByState(locationInfo.stateCode);
        altMyAddressData.value.city = locationInfo.cityCode;
        await loadAltPostcodeOptions(locationInfo.cityCode, true);
        altMyAddressData.value.postcode = newPostcode;
      } finally {
        await nextTick();
        suppressAltLocationCascade.value = false;
        isInitializingAltLocation.value = false;
      }
    }
  }
});

/** Registrable Create: altMyAddress → suppressedModelCache (altAddressData setter 루프 방지) */
function applyAltMyAddressToSuppressedCache(newValue: typeof altMyAddressData.value) {
    const countryStored =
        normalizeMyLocationFieldForPersist(newValue.country, altCountryOptions.value) ||
        String(newValue.country || '').trim();
    const stateStored =
        normalizeMyLocationFieldForPersist(newValue.state, altStateOptions.value) ||
        String(newValue.state || '').trim();
    const cityStored =
        normalizeMyLocationFieldForPersist(newValue.city, altCityOptions.value) ||
        String(newValue.city || '').trim();
    const postcodeName = newValue.postcode
        ? mergedAltPostcodeAutocompleteItems.value.find((opt) => opt.TOTAL_CODE === newValue.postcode)
              ?.CODE_NAME || ''
        : '';

    const altLocalAddressObj: Record<string, unknown> = {
        addressLine1: newValue.addressLine1 || '',
        addressLine2: newValue.addressLine2 || '',
        addressLine3: newValue.addressLine3 || '',
        country: countryStored,
        state: stateStored,
        city: cityStored,
        postcode: postcodeName,
        post_code: postcodeName,
        countryCode: countryStored,
        stateCode: stateStored,
        cityCode: cityStored,
        postcodeCode: newValue.postcode || '',
    };

    suppressedModelCache.value = {
        ...suppressedModelCache.value,
        ALT_ADDRESS_LINE_1: newValue.addressLine1 || '',
        ALT_ADDRESS_LINE_2: newValue.addressLine2 || '',
        ALT_ADDRESS_LINE_3: newValue.addressLine3 || '',
        ALT_COUNTRY: countryStored,
        ALT_STATE: stateStored,
        ALT_CITY: cityStored,
        ALT_POSTCODE: postcodeName,
        ALT_COUNTRY_CODE: countryStored,
        ALT_STATE_CODE: stateStored,
        ALT_CITY_CODE: cityStored,
        ALT_POSTCODE_CODE: newValue.postcode || '',
        altLocalAddress: altLocalAddressObj,
    };
}

// Alternative Address용 MY 주소 데이터 변경 시 altAddressData 업데이트
watch(() => altMyAddressData.value, (newValue) => {
  if (isSyncingAltMyAddress.value) return;
  if (isAddressPickerDialogOpen()) return;
  if (isInitializingAltLocation.value) return;
  if (isJurisdictionMY.value && altAddressData.value.addressType === 'B07001') {
    isSyncingAltMyAddress.value = true;
    try {
      if (props.suppressParentEmit) {
        applyAltMyAddressToSuppressedCache(newValue);
        return;
      }
      const countryStored = normalizeMyLocationFieldForPersist(
          newValue.country,
          altCountryOptions.value
      );
      const stateStored = normalizeMyLocationFieldForPersist(newValue.state, altStateOptions.value);
      const cityStored = normalizeMyLocationFieldForPersist(newValue.city, altCityOptions.value);
      const postcodeName = newValue.postcode
          ? (mergedAltPostcodeAutocompleteItems.value.find((opt) => opt.TOTAL_CODE === newValue.postcode)
                ?.CODE_NAME || '')
          : '';
      
      altAddressData.value = {
        ...altAddressData.value,
        localAddress: {
          ...altAddressData.value.localAddress,
          addressLine1: newValue.addressLine1 || '',
          addressLine2: newValue.addressLine2 || '',
          addressLine3: newValue.addressLine3 || '',
          country: countryStored,
          state: stateStored,
          city: cityStored,
          postcode: postcodeName,
          countryCode: countryStored,
          stateCode: stateStored,
          cityCode: cityStored,
          postcodeCode: newValue.postcode || '',
          post_code: postcodeName || ''
        } as any
      };
    } finally {
      nextTick(() => {
        isSyncingAltMyAddress.value = false;
      });
    }
  }
}, { deep: true });

// Alternative Address: localAddress 최초 수신 시 1회만 바인딩 (반복 API 호출 방지)
watch(
    () => altAddressData.value.localAddress,
    async (newValue) => {
        if (props.suppressParentEmit) return;
        if (isSyncingAltMyAddress.value || altMyAddressBound.value) return;
        if (isAddressPickerDialogOpen()) return;
        if (!isJurisdictionMY.value || !newValue) return;
        await loadAltMyOptions();
        altMyAddressBound.value = true;
    },
    { deep: true }
);

// Alternative Address Postal Code 검색
const isAltPostalCodeSearched = ref(false);
const isAltSearching = ref(false);
const altPostalCodeError = ref<string | null>(null);

const searchAltPostalCode = async () => {
    if (!altAddressData.value.localAddress?.postalCode || altAddressData.value.localAddress.postalCode.trim().length === 0) {
        return;
    }
    
    isAltSearching.value = true;
    altPostalCodeError.value = null;
    
    try {
        const response = await contactsApi.searchPostalCode(altAddressData.value.localAddress.postalCode);
        const result = (response as any).data?.data || (response as any).data;
        
        if (result) {
            const streetName = result.hasOwnProperty('streetName') ? (result.streetName || '') : '';
            const buildingName = result.hasOwnProperty('buildingEstateName') ? (result.buildingEstateName || '') : '';
            const blockNo = result.hasOwnProperty('blockHouseNo') ? (result.blockHouseNo || '') : '';

            if (isAddressPickerDialogOpen()) return;

            altAddressData.value = {
                ...altAddressData.value,
                localAddress: {
                    ...altAddressData.value.localAddress,
                    postalCode: altAddressData.value.localAddress?.postalCode || '',
                    streetName,
                    buildingEstateName: buildingName,
                    blockHouseNo: blockNo,
                    level: altAddressData.value.localAddress?.level || '',
                    unit: altAddressData.value.localAddress?.unit || ''
                }
            };
            
            isAltPostalCodeSearched.value = true;
            altPostalCodeError.value = null;
        }
    } catch (error: any) {
        const responseData = error?.response?.data;
        let errorMessage = '';
        
        if (responseData?.error) {
            if (typeof responseData.error === 'object' && responseData.error.message) {
                errorMessage = responseData.error.message;
            } else if (typeof responseData.error === 'string') {
                errorMessage = responseData.error;
            } else {
                errorMessage = responseData.error;
            }
        }
        
        if (!errorMessage) {
            errorMessage = responseData?.message || error?.message || '';
        }
        
        altPostalCodeError.value = errorMessage;
        console.error('Alternative Postal code search failed:', error);
    } finally {
        isAltSearching.value = false;
    }
};
</script>

<template>
    <v-card class="contact-info-section" :class="{ 'register-business-address-host': useRegisterBusinessOnlyLayout }">
        <v-card-title v-if="!addressSectionsOnly" class="contact-section-title">
            {{ t('contact.Contact') || 'Contact' }}
        </v-card-title>
        <v-card-title
            v-else-if="addressSectionsOnly && hideServiceAddress && !useRegisterAddressCardStyle"
            class="contact-section-title d-flex justify-space-between align-center flex-wrap ga-2"
        >
            <span>{{ t('contact.Business Address') || 'Business Address' }}</span>
            <v-btn
                v-show="canEditAddressItem"
                size="small"
                variant="text"
                color="primary"
                @click="addBusinessAddressRow"
            >
                <v-icon start size="16">mdi-plus</v-icon>
                {{ t('contact.Add') || 'Add' }}
            </v-btn>
        </v-card-title>
        <v-card-text>
            <v-row v-if="!addressSectionsOnly" dense>
                <!-- Country Code, Area Code (non-MY), Mobile No -->
                <v-col cols="12" :sm="contactPhoneRowColSm">
                    <!-- readonly일 때는 일반 텍스트 필드로 표시 -->
                    <v-text-field
                        v-if="isFieldReadonly('countryCode')"
                        key="contact-country-code-ro"
                        :model-value="countryCodeDisplayName"
                        :label="t('contact.Country Code') || 'Country Code'"
                        readonly
                        :class="{ 'readonly-field': isFieldReadonly('countryCode') }"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                    />
                    <!-- 수정 가능할 때는 autocomplete 사용 -->
                    <v-autocomplete
                        v-else
                        key="contact-country-code-ed"
                        :model-value="contactInfo.countryCode"
                        @update:model-value="updateCountryCode"
                        :items="countryCodeOptions"
                        item-title="CODE_NAME"
                        item-value="TOTAL_CODE"
                        :label="t('contact.Country Code') || 'Country Code'"
                        variant="outlined"
                        density="comfortable"
                        placeholder="Please Select"
                        hide-details
                        clearable
                        :search="countryCodeSearch"
                        @update:search="updateCountryCodeSearch"
                    />
                </v-col>
                
                <v-col v-if="!isJurisdictionMY" cols="12" sm="4">
                    <v-text-field
                        v-model="contactInfo.areaCode"
                        :label="t('contact.Area Code') || 'Area Code'"
                        :readonly="isFieldReadonly('areaCode')"
                        :class="{ 'readonly-field': isFieldReadonly('areaCode') }"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                    />
                </v-col>
                
                <!-- 첫 번째 Mobile No -->
                <v-col
                    cols="12"
                    :sm="contactPhoneRowColSm"
                    v-if="contactInfo.mobileNos && contactInfo.mobileNos.length > 0"
                >
                    <v-text-field
                        v-model="contactInfo.mobileNos[0]"
                        :label="getMobileLabel(0)"
                        :readonly="isFieldReadonly('mobileNos')"
                        :class="{ 'readonly-field': isFieldReadonly('mobileNos') }"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                    >
                        <template #append-inner>
                            <SyncFieldIndicator :change="syncChange('mobile_no', 'mobileNo', 'mobile_no_country_code')" />
                        </template>
                        <template v-if="!readonly && contactInfo.mobileNos.length > 1" v-slot:append>
                            <v-btn
                                icon="mdi-minus"
                                size="small"
                                variant="text"
                                @click="removeMobile(0)"
                            />
                        </template>
                    </v-text-field>
                </v-col>
                
                <v-col cols="12">
                    <template
                        v-for="(email, index) in contactInfo.emails"
                        :key="`email-row-${index}`"
                    >
                        <div class="d-flex align-center mb-2">
                            <!-- MAIN_YN 체크박스 (이메일 왼쪽) -->
                            <v-checkbox
                                v-if="!isEmailFieldReadonly(index)"
                                :model-value="contactInfo.emailMainYn?.[index] ?? (index === 0)"
                                :disabled="isPrimaryCheckboxDisabled(index)"
                                density="compact"
                                hide-details
                                class="mr-2"
                                @update:model-value="updateEmailMainYn(index, $event ?? false)"
                            />
                            <!-- 이메일 입력 필드 -->
                            <v-text-field
                                v-model="contactInfo.emails[index]"
                                :rules="isEmailFieldReadonly(index) ? EMPTY_EMAIL_RULES : (index === 0 ? emailRules : optionalEmailRules)"
                                :readonly="isEmailFieldReadonly(index)"
                                :disabled="isPrimaryEmailDisabled(index)"
                                :class="{ 'readonly-field': isEmailFieldReadonly(index) }"
                                type="email"
                                variant="outlined"
                                density="comfortable"
                                :hide-details="isEmailFieldReadonly(index) ? true : false"
                                class="flex-grow-1"
                            >
                                <template v-if="index === 0" #append-inner>
                                    <SyncFieldIndicator :change="syncChange('email', 'contactEmail')" />
                                </template>
                                <template v-slot:label>
                                    <span>{{ getEmailLabel(index) }}<span v-if="index === 0 && requireEmail" class="required-asterisk"> *</span></span>
                                </template>
                            </v-text-field>
                            <div class="d-flex" v-if="!isEmailFieldReadonly(index)">
                                <v-btn
                                    v-if="contactInfo.emails.length > 1"
                                    icon="mdi-minus"
                                    size="small"
                                    variant="text"
                                    class="ml-2"
                                    @click="removeEmail(index)"
                                />
                                <v-btn
                                    v-if="index === contactInfo.emails.length - 1 && contactInfo.emails.length < 2"
                                    icon="mdi-plus"
                                    size="small"
                                    variant="text"
                                    class="ml-1"
                                    @click="addEmail"
                                />
                            </div>
                            <!-- Sync 뱃지 (E-Mail 1이고 SYNC_YN이 true일 때만 표시, From Manual인 경우 제외) -->
                            <v-chip
                                v-if="showSyncBadge && index === 0 && !isFromManual && contactInfo.emailSyncYn?.[index] === true && email && email.trim() !== ''"
                                color="primary"
                                variant="flat"
                                size="small"
                                class="ml-2"
                            >
                                {{ t('contact.Sync') || 'Sync' }}
                            </v-chip>
                        </div>
                    </template>
                </v-col>
            </v-row>

            <v-row v-if="!hideAddressSections" dense>
                <!-- Service / Business Address (MY) -->
                <v-col cols="12" :class="addressSectionsOnly ? '' : 'mt-4'">
                    <!-- Postal Code 검색 에러 메시지 -->
                    <v-alert
                        v-if="altAddressData.addressType === 'B07001' && altPostalCodeError"
                        type="error"
                        density="compact"
                        class="mb-4"
                        closable
                        @click:close="altPostalCodeError = null"
                    >
                        {{ altPostalCodeError }}
                    </v-alert>

                    <div v-show="altAddressData.addressType === 'B07001'">
                        <!-- MY인 경우 Entity 스타일 주소 필드 -->
                        <div v-show="isJurisdictionMY">
                            <template v-if="!hideServiceAddress">
                            <div class="d-flex align-center ga-1 flex-wrap mb-3">
                                <div class="text-subtitle-2">{{ t('contact.Service Address') || 'Service Address' }}</div>
                                <v-spacer />
                                <v-btn
                                    v-show="canEditAddressItem"
                                    size="small"
                                    variant="text"
                                    color="primary"
                                    @click="addServiceAddressRow"
                                >
                                    <v-icon start size="16">mdi-plus</v-icon>
                                    {{ t('contact.Add') || 'Add' }}
                                </v-btn>
                            </div>

                            <v-alert
                                v-show="showServiceAddressEmptyPlaceholder"
                                type="info"
                                variant="tonal"
                                density="comfortable"
                                class="mb-2"
                            >
                                <div class="d-flex justify-end align-center flex-wrap gap-2">
                                    <div class="d-flex align-center ga-1 flex-wrap text-right">
                                        <v-icon
                                            v-if="props.showServiceMultiHint"
                                            size="18"
                                            color="warning"
                                            icon="mdi-alert-circle-outline"
                                        />
                                        <span class="text-caption text-medium-emphasis" v-if="props.showServiceMultiHint">
                                            {{ props.serviceMultiHintText }}
                                        </span>
                                        <span v-else>-</span>
                                    </div>
                                    <v-btn
                                        v-if="props.showServiceMultiHint && props.onReSyncAddressClick"
                                        size="small"
                                        variant="tonal"
                                        color="primary"
                                        rounded="pill"
                                        prepend-icon="mdi-sync"
                                        class="resync-action-btn"
                                        @click="props.onReSyncAddressClick?.('Service')"
                                    >
                                        Address Consolidation
                                    </v-btn>
                                </div>
                            </v-alert>

                            <div v-show="serviceAddressDraftList.length > 0" class="mb-4">
                                <div
                                    v-for="(address, index) in serviceAddressDraftList"
                                    :key="`service-json-${index}`"
                                    class="mb-3"
                                >
                                    <div
                                        v-if="!useRegisterAddressCardStyle || isAddressEditing('service', index)"
                                        class="d-flex justify-end align-center ga-1 mb-1"
                                    >
                                        <v-btn
                                            v-show="!useBranchAddresses && !useRegisterAddressCardStyle && canEditAddressItem"
                                            size="small"
                                            variant="text"
                                            color="error"
                                            @click="removeServiceAddressRow(index)"
                                        >
                                            <v-icon start size="16">mdi-delete-outline</v-icon>
                                            {{ t('contact.Delete') || 'Delete' }}
                                        </v-btn>
                                        <v-btn
                                            v-show="useBranchAddresses && canEditAddressItem && isAddressEditing('service', index)"
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            @click.stop="openBranchShowAddress('service', index)"
                                        >
                                            <v-icon start size="16">mdi-map-marker-multiple</v-icon>
                                            {{ t('contact.Show Address') || 'Show Address' }}
                                        </v-btn>
                                        <v-btn
                                            v-show="!useBranchAddresses && contactCdForSelect && isJurisdictionMY && canEditAddressItem"
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            @click.stop="openJsonSelectAddress('service', index)"
                                        >
                                            <v-icon start size="16">mdi-map-marker-check</v-icon>
                                            {{ t('contact.Select Address') || 'Select Address' }}
                                        </v-btn>
                                        <v-btn
                                            v-show="canEditAddressItem && !contactJsonAddressSyncEditLocked(address) && (!useRegisterAddressCardStyle || isAddressEditing('service', index))"
                                            size="small"
                                            variant="text"
                                            color="primary"
                                            @click="toggleAddressEdit('service', index)"
                                        >
                                            <v-icon start size="16">{{ isAddressEditing('service', index) ? 'mdi-close' : 'mdi-pencil' }}</v-icon>
                                            {{ isAddressEditing('service', index) ? ((t('contact.Cancel') || 'Cancel')) : ((t('contact.Edit') || 'Edit')) }}
                                        </v-btn>
                                    </div>

                                    <v-card
                                        v-if="useRegisterAddressCardStyle && !isAddressEditing('service', index)"
                                        variant="outlined"
                                        rounded="lg"
                                        class="register-address-card mb-2"
                                    >
                                        <div class="register-address-card-body">
                                            <div class="register-address-card-main">
                                                <div class="register-address-card-title">{{ t('contact.Service Address') || 'Service Address' }}</div>
                                                <div class="register-address-card-text">
                                                    <MyBranchAddressDisplayLine
                                                        :address="address"
                                                        :country-opts="altCountryOptions"
                                                        :state-opts="altStateOptions"
                                                        :city-opts="altCityOptions"
                                                        :postcode-opts="mergedAltPostcodeAutocompleteItems"
                                                    />
                                                </div>
                                            </div>
                                            <div class="d-flex align-center ga-2 flex-shrink-0">
                                                <v-btn
                                                    v-if="canEditAddressItem && !contactJsonAddressSyncEditLocked(address)"
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    rounded="pill"
                                                    class="register-address-action-btn"
                                                    @click="toggleAddressEdit('service', index)"
                                                >
                                                    <v-icon start size="16">mdi-pencil</v-icon>
                                                    {{ t('contact.Edit') || 'Edit' }}
                                                </v-btn>
                                                <v-btn
                                                    v-if="canEditAddressItem"
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    rounded="pill"
                                                    class="register-address-action-btn"
                                                    :loading="isDeleting(`service-${index}`)"
                                                    @click.stop="removeServiceAddressRow(index)"
                                                >
                                                    {{ t('contact.Delete') || 'Delete' }}
                                                </v-btn>
                                            </div>
                                        </div>
                                    </v-card>

                                    <v-alert
                                        v-show="!useRegisterAddressCardStyle && !isAddressEditing('service', index)"
                                        type="info"
                                        variant="tonal"
                                        density="comfortable"
                                        class="mb-2"
                                    >
                                        <div class="d-flex justify-space-between align-center flex-wrap gap-2">
                                            <div class="d-flex align-center flex-wrap flex-grow-1 ga-2">
                                                <MyBranchAddressDisplayLine
                                                    :address="address"
                                                    :country-opts="altCountryOptions"
                                                    :state-opts="altStateOptions"
                                                    :city-opts="altCityOptions"
                                                    :postcode-opts="mergedAltPostcodeAutocompleteItems"
                                                >
                                                    <v-chip
                                                        v-if="isAddressSyncFlag(address)"
                                                        color="primary"
                                                        variant="flat"
                                                        size="small"
                                                        class="flex-shrink-0"
                                                    >
                                                        {{ t('contact.Sync') || 'Sync' }}
                                                    </v-chip>
                                                </MyBranchAddressDisplayLine>
                                                <v-btn
                                                    v-if="useBranchAddresses && canEditAddressItem"
                                                    icon
                                                    variant="outlined"
                                                    size="small"
                                                    color="error"
                                                    density="comfortable"
                                                    class="flex-shrink-0"
                                                    :loading="isDeleting(`service-${index}`)"
                                                    :aria-label="t('contact.Delete') || 'Delete'"
                                                    @click.stop="removeServiceAddressRow(index)"
                                                >
                                                    <v-icon size="20">mdi-minus-circle-outline</v-icon>
                                                </v-btn>
                                            </div>
                                            <div
                                                v-if="index === 0 && props.showServiceMultiHint"
                                                class="d-flex align-center ga-2 flex-wrap"
                                            >
                                                <div class="d-flex align-center ga-1 flex-wrap text-right">
                                                    <v-icon size="18" color="warning" icon="mdi-alert-circle-outline" />
                                                    <span class="text-caption text-medium-emphasis">{{ props.serviceMultiHintText }}</span>
                                                </div>
                                                <v-btn
                                                    v-if="props.onReSyncAddressClick"
                                                    size="small"
                                                    variant="tonal"
                                                    color="primary"
                                                    rounded="pill"
                                                    prepend-icon="mdi-sync"
                                                    class="resync-action-btn"
                                                    @click="props.onReSyncAddressClick?.('Service')"
                                                >
                                                    Address Consolidation
                                                </v-btn>
                                            </div>
                                        </div>
                                    </v-alert>

                                    <div v-if="isAddressEditing('service', index)" class="mb-2">
                                        <v-row v-if="useBranchAddresses && !useRegisterAddressCardStyle" dense>
                                            <v-col cols="12">
                                                <v-text-field v-model="address.alias" :label="t('contact.Alias') || 'Alias'" variant="outlined" density="comfortable" hide-details clearable />
                                            </v-col>
                                        </v-row>
                                        <v-row dense>
                                            <v-col cols="12">
                                                <v-text-field v-model="address.street01" :label="t('contact.Address Line 1') || 'Address Line 1'" variant="outlined" density="comfortable" hide-details />
                                            </v-col>
                                        </v-row>
                                        <v-row dense>
                                            <v-col cols="12">
                                                <v-text-field v-model="address.street02" :label="t('contact.Address Line 2') || 'Address Line 2'" variant="outlined" density="comfortable" hide-details />
                                            </v-col>
                                        </v-row>
                                        <v-row dense>
                                            <v-col cols="12">
                                                <v-text-field v-model="address.street03" :label="t('contact.Address Line 3') || 'Address Line 3'" variant="outlined" density="comfortable" hide-details />
                                            </v-col>
                                        </v-row>
                                        <v-row dense>
                                            <v-col cols="12" md="6">
                                                <v-text-field
                                                    v-if="isJsonAddressRowReadonly('service', index)"
                                                    v-model="address.country"
                                                    :label="t('contact.Country') || 'Country'"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    hide-details
                                                />
                                                <v-autocomplete
                                                    v-else
                                                    v-model="address.country"
                                                    v-model:search="address._countrySearch"
                                                    :items="altCountryOptions"
                                                    :label="t('contact.Country') || 'Country'"
                                                    item-title="CODE_NAME"
                                                    item-value="TOTAL_CODE"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :placeholder="t('contact.Please Select') || 'Please Select'"
                                                    hide-details
                                                    clearable
                                                    :auto-select-first="false"
                                                    @focus="onJsonAddressCountryFocus(address)"
                                                    @update:model-value="onJsonAddressCountrySelect(address, $event)"
                                                />
                                            </v-col>
                                            <v-col v-if="serviceRowShowLocationFields[index]" cols="12" md="6">
                                                <v-text-field
                                                    v-if="isJsonAddressRowReadonly('service', index)"
                                                    v-model="address.state"
                                                    :label="t('contact.State') || 'State'"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    hide-details
                                                />
                                                <v-autocomplete
                                                    v-else
                                                    v-model="address.state"
                                                    :items="altStateOptions"
                                                    :label="t('contact.State') || 'State'"
                                                    item-title="CODE_NAME"
                                                    item-value="TOTAL_CODE"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :placeholder="t('contact.Please Select') || 'Please Select'"
                                                    hide-details
                                                    clearable
                                                    @update:model-value="queueContactJsonAddressState(address, $event)"
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-row v-if="serviceRowShowLocationFields[index]" dense>
                                            <v-col cols="12" md="6">
                                                <v-text-field
                                                    v-if="isJsonAddressRowReadonly('service', index)"
                                                    v-model="address.city"
                                                    :label="t('contact.City') || 'City'"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    hide-details
                                                />
                                                <v-autocomplete
                                                    v-else
                                                    v-model="address.city"
                                                    :items="altCityOptions"
                                                    :label="t('contact.City') || 'City'"
                                                    item-title="CODE_NAME"
                                                    item-value="TOTAL_CODE"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :placeholder="t('contact.Please Select') || 'Please Select'"
                                                    hide-details
                                                    clearable
                                                    :loading="isLoadingAltCities"
                                                    @focus="ensureAltCitiesLoadedForState(address.state)"
                                                    @click:control="ensureAltCitiesLoadedForState(address.state)"
                                                    @update:model-value="queueContactJsonAddressCity(address, $event)"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="6">
                                                <v-text-field
                                                    v-if="isJsonAddressRowReadonly('service', index)"
                                                    v-model="address.post_code"
                                                    :label="t('contact.Postcode') || 'Postcode'"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    hide-details
                                                />
                                                <v-autocomplete
                                                    v-else
                                                    v-model="address.post_code"
                                                    :items="getContactJsonPostcodeComboItems(address)"
                                                    :label="t('contact.Postcode') || 'Postcode'"
                                                    item-title="CODE_NAME"
                                                    item-value="TOTAL_CODE"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :placeholder="t('contact.Please Select') || 'Please Select'"
                                                    hide-details
                                                    clearable
                                                    @update:menu="$event && void ensureAltPostcodeFullCacheForReverse()"
                                                    @update:model-value="() => queueContactJsonAddressPostcode(address)"
                                                />
                                            </v-col>
                                        </v-row>
                                    </div>
                                </div>
                            </div>

                            <v-alert
                                v-show="showLegacyServiceSummary"
                                type="info"
                                variant="tonal"
                                density="comfortable"
                                class="mb-4 my-address-summary"
                            >
                                <div class="d-flex align-center flex-wrap gap-2">
                                    <span class="flex-grow-1 text-wrap">{{ mergedAltMyAddress || '-' }}</span>
                                    <v-chip
                                        v-if="isAddressSyncFlag((props.modelValue as any)?.altLocalAddress)"
                                        color="primary"
                                        variant="flat"
                                        size="small"
                                        class="flex-shrink-0"
                                    >
                                        {{ t('contact.Sync') || 'Sync' }}
                                    </v-chip>
                                    <v-btn
                                        v-show="canEditAltMyAddress"
                                        size="small"
                                        variant="text"
                                        color="primary"
                                        class="flex-shrink-0"
                                        @click="toggleAltMyAddressEdit"
                                    >
                                        <v-icon start size="16">mdi-pencil</v-icon>
                                        {{ t('contact.Edit') || 'Edit' }}
                                    </v-btn>
                                </div>
                            </v-alert>

                            <div v-show="showLegacyServiceEditForm">
                                <v-row v-if="isBizFileRegistrant && isAltMyAddressEditing" dense class="mb-1">
                                    <v-col cols="12" class="d-flex justify-end">
                                        <v-btn
                                            size="small"
                                            variant="text"
                                            color="primary"
                                            @click="toggleAltMyAddressEdit"
                                        >
                                            <v-icon start size="16">mdi-close</v-icon>
                                            {{ t('contact.Cancel') || 'Cancel' }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                                <!-- Address Line 1 -->
                                <v-row dense>
                                    <v-col cols="12">
                                        <v-text-field 
                                            v-model="altMyAddressData.addressLine1" 
                                            :label="t('contact.Address Line 1') || 'Address Line 1'" 
                                            :readonly="isLegacyAltMyAddressLineReadonly"
                                            :class="{ 'readonly-field': isLegacyAltMyAddressLineReadonly }"
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details 
                                        />
                                    </v-col>
                                </v-row>
                                
                                <!-- Address Line 2 -->
                                <v-row dense>
                                    <v-col cols="12">
                                        <v-text-field 
                                            v-model="altMyAddressData.addressLine2" 
                                            :label="t('contact.Address Line 2') || 'Address Line 2'" 
                                            :readonly="isLegacyAltMyAddressLineReadonly"
                                            :class="{ 'readonly-field': isLegacyAltMyAddressLineReadonly }"
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details 
                                        />
                                    </v-col>
                                </v-row>
                                
                                <!-- Address Line 3 -->
                                <v-row dense>
                                    <v-col cols="12">
                                        <v-text-field 
                                            v-model="altMyAddressData.addressLine3" 
                                            :label="t('contact.Address Line 3') || 'Address Line 3'" 
                                            :readonly="isLegacyAltMyAddressLineReadonly"
                                            :class="{ 'readonly-field': isLegacyAltMyAddressLineReadonly }"
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details 
                                        />
                                    </v-col>
                                </v-row>
                                
                                <!-- Country -->
                                <v-row dense>
                                    <v-col cols="12" md="6">
                                        <!-- Readonly일 때는 텍스트 필드로 표시 -->
                                        <v-text-field
                                            v-if="isLegacyAltMyAddressLineReadonly"
                                            :model-value="getAltCountryDisplayName"
                                            :label="t('contact.Country') || 'Country'"
                                            :readonly="true"
                                            :class="{ 'readonly-field': true }"
                                            variant="outlined"
                                            density="comfortable"
                                            hide-details
                                        />
                                        <v-autocomplete
                                            v-else
                                            v-model="altMyAddressData.country"
                                            v-model:search="altCountrySearch"
                                            :items="altCountryOptions"
                                            :label="t('contact.Country') || 'Country'"
                                            item-title="CODE_NAME"
                                            item-value="TOTAL_CODE"
                                            variant="outlined"
                                            density="comfortable"
                                            :placeholder="t('contact.Please Select') || 'Please Select'"
                                            hide-details
                                            clearable
                                            :auto-select-first="false"
                                            @focus="onAltCountryFocus"
                                            @update:model-value="onAltMyAddressCountryUpdate"
                                        />
                                    </v-col>
                                    
                                    <!-- State -->
                                    <v-col v-if="shouldShowAltLocationFields" cols="12" md="6">
                                        <!-- Readonly일 때는 텍스트 필드로 표시 -->
                                        <v-text-field
                                            v-if="isLegacyAltMyAddressLineReadonly"
                                            :model-value="getAltStateDisplayName"
                                            :label="t('contact.State') || 'State'"
                                            :readonly="true"
                                            :class="{ 'readonly-field': true }"
                                            variant="outlined"
                                            density="comfortable"
                                            hide-details
                                        />
                                        <v-autocomplete
                                            v-else
                                            v-model="altMyAddressData.state"
                                            :items="altStateOptions"
                                            :label="t('contact.State') || 'State'"
                                            item-title="CODE_NAME"
                                            item-value="TOTAL_CODE"
                                            variant="outlined"
                                            density="comfortable"
                                            :placeholder="t('contact.Please Select') || 'Please Select'"
                                            hide-details
                                            clearable
                                        />
                                    </v-col>
                                </v-row>
                                
                                <!-- City, Postcode -->
                                <v-row v-if="shouldShowAltLocationFields" dense>
                                    <v-col cols="12" md="6">
                                        <!-- Readonly일 때는 텍스트 필드로 표시 -->
                                        <v-text-field
                                            v-if="isLegacyAltMyAddressLineReadonly"
                                            :model-value="getAltCityDisplayName"
                                            :label="t('contact.City') || 'City'"
                                            :readonly="true"
                                            :class="{ 'readonly-field': true }"
                                            variant="outlined"
                                            density="comfortable"
                                            hide-details
                                        />
                                        <v-autocomplete
                                            v-else
                                            v-model="altMyAddressData.city"
                                            :items="altCityOptions"
                                            :label="t('contact.City') || 'City'"
                                            item-title="CODE_NAME"
                                            item-value="TOTAL_CODE"
                                            variant="outlined"
                                            density="comfortable"
                                            :placeholder="t('contact.Please Select') || 'Please Select'"
                                            hide-details
                                            clearable
                                            :loading="isLoadingAltCities"
                                            @focus="ensureAltCitiesLoadedForState(altMyAddressData.state)"
                                            @click:control="ensureAltCitiesLoadedForState(altMyAddressData.state)"
                                        />
                                    </v-col>
                                    
                                    <v-col cols="12" md="6">
                                        <!-- Readonly일 때는 텍스트 필드로 표시 -->
                                        <v-text-field
                                            v-if="isLegacyAltMyAddressLineReadonly"
                                            :model-value="getAltPostcodeDisplayName"
                                            :label="t('contact.Postcode') || 'Postcode'"
                                            :readonly="true"
                                            :class="{ 'readonly-field': true }"
                                            variant="outlined"
                                            density="comfortable"
                                            hide-details
                                        />
                                        <v-autocomplete
                                            v-else
                                            v-model="altMyAddressData.postcode"
                                            :items="altLegacyPostcodeAutocompleteItems"
                                            :label="t('contact.Postcode') || 'Postcode'"
                                            item-title="CODE_NAME"
                                            item-value="TOTAL_CODE"
                                            variant="outlined"
                                            density="comfortable"
                                            :placeholder="t('contact.Please Select') || 'Please Select'"
                                            hide-details
                                            clearable
                                            :loading="isLoadingAltPostcodes"
                                            @update:menu="$event && void ensureAltPostcodeFullCacheForReverse()"
                                        />
                                    </v-col>
                                </v-row>
                            </div>
                            </template>

                            <div v-if="!hideBusinessAddressSection">
                                <div
                                    v-if="!(addressSectionsOnly && hideServiceAddress)"
                                    class="d-flex align-center ga-1 flex-wrap mb-3 mt-4"
                                >
                                    <div class="text-subtitle-2">{{ t('contact.Business Address') || 'Business Address' }}</div>
                                    <v-spacer />
                                    <v-btn
                                        v-show="canEditAddressItem"
                                        size="small"
                                        variant="text"
                                        color="primary"
                                        @click="addBusinessAddressRow"
                                    >
                                        <v-icon start size="16">mdi-plus</v-icon>
                                        {{ t('contact.Add') || 'Add' }}
                                    </v-btn>
                                </div>

                                <div class="mb-4">
                                    <v-card
                                        v-if="useRegisterAddressCardStyle && businessAddressDraftList.length === 0"
                                        variant="outlined"
                                        rounded="lg"
                                        class="register-address-card mb-2"
                                    >
                                        <div class="register-address-card-body">
                                            <div class="register-address-card-main">
                                                <div class="register-address-card-title">{{ t('contact.Business Address') || 'Business Address' }}</div>
                                                <div class="register-address-card-text">-</div>
                                            </div>
                                            <div class="d-flex align-center ga-2 flex-shrink-0">
                                                <v-btn
                                                    v-if="canEditAddressItem"
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    rounded="pill"
                                                    class="register-address-action-btn"
                                                    @click="addBusinessAddressRow"
                                                >
                                                    <v-icon start size="16">mdi-pencil</v-icon>
                                                    {{ t('contact.Edit') || 'Edit' }}
                                                </v-btn>
                                            </div>
                                        </div>
                                    </v-card>
                                    <v-alert
                                        v-show="businessAddressDraftList.length === 0 && !useRegisterAddressCardStyle"
                                        type="info"
                                        variant="tonal"
                                        density="comfortable"
                                        class="mb-2"
                                    >
                                        <div class="d-flex justify-end align-center flex-wrap gap-2">
                                            <div class="d-flex align-center ga-1 flex-wrap text-right">
                                                <v-icon
                                                    v-if="props.showBusinessMultiHint"
                                                    size="18"
                                                    color="warning"
                                                    icon="mdi-alert-circle-outline"
                                                />
                                                <span class="text-caption text-medium-emphasis" v-if="props.showBusinessMultiHint">
                                                    {{ props.businessMultiHintText }}
                                                </span>
                                                <span v-else>-</span>
                                            </div>
                                            <v-btn
                                                v-if="props.showBusinessMultiHint && props.onReSyncAddressClick"
                                                size="small"
                                                variant="tonal"
                                                color="primary"
                                                rounded="pill"
                                                prepend-icon="mdi-sync"
                                                class="resync-action-btn"
                                                @click="props.onReSyncAddressClick?.('Business / BO')"
                                            >
                                                Address Consolidation
                                            </v-btn>
                                        </div>
                                    </v-alert>
                                    <div v-for="(address, index) in businessAddressDraftList" :key="`business-editor-${index}`" class="mb-4">
                                    <div
                                        v-if="!useRegisterAddressCardStyle || isAddressEditing('business', index)"
                                        class="d-flex justify-end align-center ga-1 mb-1"
                                    >
                                        <v-btn
                                            v-show="!useBranchAddresses && !useRegisterAddressCardStyle && canEditAddressItem"
                                            size="small"
                                            variant="text"
                                            color="error"
                                            @click="removeBusinessAddressRow(index)"
                                        >
                                            <v-icon start size="16">mdi-delete-outline</v-icon>
                                            {{ t('contact.Delete') || 'Delete' }}
                                        </v-btn>
                                        <v-btn
                                            v-show="useBranchAddresses && canEditAddressItem && isAddressEditing('business', index)"
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            @click.stop="openBranchShowAddress('business', index)"
                                        >
                                            <v-icon start size="16">mdi-map-marker-multiple</v-icon>
                                            {{ t('contact.Show Address') || 'Show Address' }}
                                        </v-btn>
                                        <v-btn
                                            v-show="!useBranchAddresses && contactCdForSelect && isJurisdictionMY && canEditAddressItem"
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            @click.stop="openJsonSelectAddress('business', index)"
                                        >
                                            <v-icon start size="16">mdi-map-marker-check</v-icon>
                                            {{ t('contact.Select Address') || 'Select Address' }}
                                        </v-btn>
                                        <v-btn
                                            v-show="canEditAddressItem && !contactJsonAddressSyncEditLocked(address) && (!useRegisterAddressCardStyle || isAddressEditing('business', index))"
                                            size="small"
                                            variant="text"
                                            color="primary"
                                            @click="toggleAddressEdit('business', index)"
                                        >
                                            <v-icon start size="16">{{ isAddressEditing('business', index) ? 'mdi-close' : 'mdi-pencil' }}</v-icon>
                                            {{ isAddressEditing('business', index) ? ((t('contact.Cancel') || 'Cancel')) : ((t('contact.Edit') || 'Edit')) }}
                                        </v-btn>
                                    </div>
                                    <v-card
                                        v-if="useRegisterAddressCardStyle && !isAddressEditing('business', index)"
                                        variant="outlined"
                                        rounded="lg"
                                        class="register-address-card mb-2"
                                    >
                                        <div class="register-address-card-body">
                                            <div class="register-address-card-main">
                                                <div class="register-address-card-title">{{ t('contact.Business Address') || 'Business Address' }}</div>
                                                <div class="register-address-card-text">
                                                    <MyBranchAddressDisplayLine
                                                        :address="address"
                                                        :country-opts="altCountryOptions"
                                                        :state-opts="altStateOptions"
                                                        :city-opts="altCityOptions"
                                                        :postcode-opts="mergedAltPostcodeAutocompleteItems"
                                                    />
                                                </div>
                                            </div>
                                            <div class="d-flex align-center ga-2 flex-shrink-0">
                                                <v-btn
                                                    v-if="canEditAddressItem && !contactJsonAddressSyncEditLocked(address)"
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    rounded="pill"
                                                    class="register-address-action-btn"
                                                    @click="toggleAddressEdit('business', index)"
                                                >
                                                    <v-icon start size="16">mdi-pencil</v-icon>
                                                    {{ t('contact.Edit') || 'Edit' }}
                                                </v-btn>
                                                <v-btn
                                                    v-if="canEditAddressItem"
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    rounded="pill"
                                                    class="register-address-action-btn"
                                                    :loading="isDeleting(`business-${index}`)"
                                                    @click.stop="removeBusinessAddressRow(index)"
                                                >
                                                    {{ t('contact.Delete') || 'Delete' }}
                                                </v-btn>
                                            </div>
                                        </div>
                                    </v-card>
                                    <v-alert
                                        v-show="!useRegisterAddressCardStyle && !isAddressEditing('business', index)"
                                        type="info"
                                        variant="tonal"
                                        density="comfortable"
                                        class="mb-2"
                                    >
                                        <div class="d-flex justify-space-between align-center flex-wrap gap-2">
                                            <div class="d-flex align-center flex-wrap flex-grow-1 ga-2">
                                                <MyBranchAddressDisplayLine
                                                    :address="address"
                                                    :country-opts="altCountryOptions"
                                                    :state-opts="altStateOptions"
                                                    :city-opts="altCityOptions"
                                                    :postcode-opts="mergedAltPostcodeAutocompleteItems"
                                                >
                                                    <v-chip
                                                        v-if="isAddressSyncFlag(address)"
                                                        color="primary"
                                                        variant="flat"
                                                        size="small"
                                                        class="flex-shrink-0"
                                                    >
                                                        {{ t('contact.Sync') || 'Sync' }}
                                                    </v-chip>
                                                </MyBranchAddressDisplayLine>
                                                <v-btn
                                                    v-if="useBranchAddresses && canEditAddressItem"
                                                    icon
                                                    variant="outlined"
                                                    size="small"
                                                    color="error"
                                                    density="comfortable"
                                                    class="flex-shrink-0"
                                                    :loading="isDeleting(`business-${index}`)"
                                                    :aria-label="t('contact.Delete') || 'Delete'"
                                                    @click.stop="removeBusinessAddressRow(index)"
                                                >
                                                    <v-icon size="20">mdi-minus-circle-outline</v-icon>
                                                </v-btn>
                                            </div>
                                            <div
                                                v-if="index === 0 && props.showBusinessMultiHint"
                                                class="d-flex align-center ga-2 flex-wrap"
                                            >
                                                <div class="d-flex align-center ga-1 flex-wrap text-right">
                                                    <v-icon size="18" color="warning" icon="mdi-alert-circle-outline" />
                                                    <span class="text-caption text-medium-emphasis">{{ props.businessMultiHintText }}</span>
                                                </div>
                                                <v-btn
                                                    v-if="props.onReSyncAddressClick"
                                                    size="small"
                                                    variant="tonal"
                                                    color="primary"
                                                    rounded="pill"
                                                    prepend-icon="mdi-sync"
                                                    class="resync-action-btn"
                                                    @click="props.onReSyncAddressClick?.('Business / BO')"
                                                >
                                                    Address Consolidation
                                                </v-btn>
                                            </div>
                                        </div>
                                    </v-alert>
                                    <div v-if="isAddressEditing('business', index)">
                                    <v-row v-if="useBranchAddresses && !useRegisterAddressCardStyle" dense>
                                        <v-col cols="12">
                                            <v-text-field
                                                v-model="address.alias"
                                                :label="t('contact.Alias') || 'Alias'"
                                                variant="outlined"
                                                density="comfortable"
                                                hide-details
                                                clearable
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row dense>
                                        <v-col cols="12">
                                            <v-text-field
                                                v-model="address.street01"
                                                :label="t('contact.Address Line 1') || 'Address Line 1'"
                                                :readonly="isJsonAddressRowReadonly('business', index)"
                                                variant="outlined"
                                                density="comfortable"
                                                hide-details
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row dense>
                                        <v-col cols="12">
                                            <v-text-field
                                                v-model="address.street02"
                                                :label="t('contact.Address Line 2') || 'Address Line 2'"
                                                :readonly="isJsonAddressRowReadonly('business', index)"
                                                variant="outlined"
                                                density="comfortable"
                                                hide-details
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row dense>
                                        <v-col cols="12">
                                            <v-text-field
                                                v-model="address.street03"
                                                :label="t('contact.Address Line 3') || 'Address Line 3'"
                                                :readonly="isJsonAddressRowReadonly('business', index)"
                                                variant="outlined"
                                                density="comfortable"
                                                hide-details
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row dense>
                                        <v-col cols="12" md="6">
                                            <v-text-field
                                                v-if="isJsonAddressRowReadonly('business', index)"
                                                v-model="address.country"
                                                :label="t('contact.Country') || 'Country'"
                                                variant="outlined"
                                                density="comfortable"
                                                hide-details
                                            />
                                            <v-autocomplete
                                                v-else
                                                v-model="address.country"
                                                v-model:search="address._countrySearch"
                                                :items="altCountryOptions"
                                                :label="t('contact.Country') || 'Country'"
                                                item-title="CODE_NAME"
                                                item-value="TOTAL_CODE"
                                                variant="outlined"
                                                density="comfortable"
                                                :placeholder="t('contact.Please Select') || 'Please Select'"
                                                hide-details
                                                clearable
                                                :auto-select-first="false"
                                                @focus="onJsonAddressCountryFocus(address)"
                                                @update:model-value="onJsonAddressCountrySelect(address, $event)"
                                            />
                                        </v-col>
                                        <v-col v-if="businessRowShowLocationFields[index]" cols="12" md="6">
                                            <v-text-field
                                                v-if="isJsonAddressRowReadonly('business', index)"
                                                v-model="address.state"
                                                :label="t('contact.State') || 'State'"
                                                variant="outlined"
                                                density="comfortable"
                                                hide-details
                                            />
                                            <v-autocomplete
                                                v-else
                                                v-model="address.state"
                                                :items="altStateOptions"
                                                :label="t('contact.State') || 'State'"
                                                item-title="CODE_NAME"
                                                item-value="TOTAL_CODE"
                                                variant="outlined"
                                                density="comfortable"
                                                :placeholder="t('contact.Please Select') || 'Please Select'"
                                                hide-details
                                                clearable
                                                @update:model-value="queueContactJsonAddressState(address, $event)"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row v-if="businessRowShowLocationFields[index]" dense>
                                        <v-col cols="12" md="6">
                                            <v-text-field
                                                v-if="isJsonAddressRowReadonly('business', index)"
                                                v-model="address.city"
                                                :label="t('contact.City') || 'City'"
                                                variant="outlined"
                                                density="comfortable"
                                                hide-details
                                            />
                                            <v-autocomplete
                                                v-else
                                                v-model="address.city"
                                                :items="altCityOptions"
                                                :label="t('contact.City') || 'City'"
                                                item-title="CODE_NAME"
                                                item-value="TOTAL_CODE"
                                                variant="outlined"
                                                density="comfortable"
                                                :placeholder="t('contact.Please Select') || 'Please Select'"
                                                hide-details
                                                clearable
                                                :loading="isLoadingAltCities"
                                                @focus="ensureAltCitiesLoadedForState(address.state)"
                                                @click:control="ensureAltCitiesLoadedForState(address.state)"
                                                @update:model-value="queueContactJsonAddressCity(address, $event)"
                                            />
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field
                                                v-if="isJsonAddressRowReadonly('business', index)"
                                                v-model="address.post_code"
                                                :label="t('contact.Postcode') || 'Postcode'"
                                                variant="outlined"
                                                density="comfortable"
                                                hide-details
                                            />
                                            <v-autocomplete
                                                v-else
                                                v-model="address.post_code"
                                                :items="getContactJsonPostcodeComboItems(address)"
                                                :label="t('contact.Postcode') || 'Postcode'"
                                                item-title="CODE_NAME"
                                                item-value="TOTAL_CODE"
                                                variant="outlined"
                                                density="comfortable"
                                                :placeholder="t('contact.Please Select') || 'Please Select'"
                                                hide-details
                                                clearable
                                                @update:menu="$event && void ensureAltPostcodeFullCacheForReverse()"
                                                @update:model-value="() => queueContactJsonAddressPostcode(address)"
                                            />
                                        </v-col>
                                    </v-row>
                                    </div>
                                </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- MY가 아닌 경우 기존 필드 -->
                        <div v-show="!isJurisdictionMY">
                            <!-- Postal Code -->
                            <v-row dense>
                                <v-col cols="12" md="4">
                                    <!-- Readonly일 때는 일반 텍스트 필드로 표시 -->
                                    <v-text-field
                                        v-if="readonly"
                                        :model-value="altAddressData.localAddress?.postalCode"
                                        :label="t('contact.Postal Code') || 'Postal Code'"
                                        :readonly="true"
                                        :class="{ 'readonly-field': true }"
                                        variant="outlined"
                                        density="comfortable"
                                        hide-details
                                    />
                                    <!-- 수정 가능할 때는 검색 아이콘 포함 -->
                                    <v-text-field
                                        v-else
                                        :model-value="altAddressData.localAddress?.postalCode"
                                        @update:model-value="(value) => altAddressData = { ...altAddressData, localAddress: { ...altAddressData.localAddress, postalCode: value || '' } }"
                                        :label="t('contact.Postal Code') || 'Postal Code'"
                                        variant="outlined"
                                        density="comfortable"
                                        hide-details
                                        append-inner-icon="mdi-magnify"
                                        :loading="isAltSearching"
                                        @click:append-inner="searchAltPostalCode"
                                    />
                                </v-col>
                            </v-row>
                            
                            <!-- Block/House No, Street Name, Building/Estate Name -->
                            <v-row dense>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        :model-value="altAddressData.localAddress?.blockHouseNo"
                                        @update:model-value="(value) => altAddressData = { ...altAddressData, localAddress: { ...altAddressData.localAddress, blockHouseNo: value || '' } }"
                                        :label="t('contact.Block/House No') || 'Block/House No'"
                                        :disabled="readonly || isAltPostalCodeSearched"
                                        :readonly="readonly || isAltPostalCodeSearched || isJurisdictionSG"
                                        variant="outlined"
                                        density="comfortable"
                                        hide-details
                                        :class="(readonly || isAltPostalCodeSearched || isJurisdictionSG) ? 'postal-readonly-field' : ''"
                                    />
                                </v-col>
                                
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        :model-value="altAddressData.localAddress?.streetName"
                                        @update:model-value="(value) => altAddressData = { ...altAddressData, localAddress: { ...altAddressData.localAddress, streetName: value || '' } }"
                                        :label="t('contact.Street Name') || 'Street Name'"
                                        :disabled="readonly || isAltPostalCodeSearched"
                                        :readonly="readonly || isAltPostalCodeSearched || isJurisdictionSG"
                                        variant="outlined"
                                        density="comfortable"
                                        hide-details
                                        :class="(readonly || isAltPostalCodeSearched || isJurisdictionSG) ? 'postal-readonly-field' : ''"
                                    />
                                </v-col>
                                
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        :model-value="altAddressData.localAddress?.buildingEstateName"
                                        @update:model-value="(value) => altAddressData = { ...altAddressData, localAddress: { ...altAddressData.localAddress, buildingEstateName: value || '' } }"
                                        :label="t('contact.Building/Estate Name') || 'Building/Estate Name'"
                                        :disabled="readonly || isAltPostalCodeSearched"
                                        :readonly="readonly || isAltPostalCodeSearched || isJurisdictionSG"
                                        variant="outlined"
                                        density="comfortable"
                                        hide-details
                                        :class="(readonly || isAltPostalCodeSearched || isJurisdictionSG) ? 'postal-readonly-field' : ''"
                                    />
                                </v-col>
                            </v-row>
                            
                            <!-- Level, Unit -->
                            <v-row dense>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        :model-value="altAddressData.localAddress?.level"
                                        @update:model-value="(value) => altAddressData = { ...altAddressData, localAddress: { ...altAddressData.localAddress, level: value || '' } }"
                                        :label="t('contact.Level') || 'Level'"
                                        :readonly="readonly"
                                        :class="{ 'readonly-field': readonly }"
                                        variant="outlined"
                                        density="comfortable"
                                        hide-details
                                    />
                                </v-col>
                                
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        :model-value="altAddressData.localAddress?.unit"
                                        @update:model-value="(value) => altAddressData = { ...altAddressData, localAddress: { ...altAddressData.localAddress, unit: value || '' } }"
                                        :label="t('contact.Unit') || 'Unit'"
                                        :readonly="readonly"
                                        :class="{ 'readonly-field': readonly }"
                                        variant="outlined"
                                        density="comfortable"
                                        hide-details
                                    />
                                </v-col>
                            </v-row>
                        </div>
                    </div>

                    <div v-show="altAddressData.addressType !== 'B07001'">
                        <!-- Foreign Address Line 1, Foreign Address Line 2 -->
                        <v-row dense>
                            <v-col cols="12">
                                <v-text-field
                                    :model-value="altAddressData.foreignAddress?.addressLine1"
                                    @update:model-value="(value) => altAddressData = { ...altAddressData, foreignAddress: { ...altAddressData.foreignAddress, addressLine1: value || '' } }"
                                    :label="t('contact.Foreign Address Line 1') || 'Foreign Address Line 1'"
                                    :readonly="readonly"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                />
                            </v-col>
                        </v-row>
                        
                        <v-row dense>
                            <v-col cols="12">
                                <v-text-field
                                    :model-value="altAddressData.foreignAddress?.addressLine2"
                                    @update:model-value="(value) => altAddressData = { ...altAddressData, foreignAddress: { ...altAddressData.foreignAddress, addressLine2: value || '' } }"
                                    :label="t('contact.Foreign Address Line 2') || 'Foreign Address Line 2'"
                                    :readonly="readonly"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                />
                            </v-col>
                        </v-row>
                    </div>
                </v-col>
                
                <!-- 나머지 Mobile No 필드들 (두 번째부터) -->
                <v-col cols="12" v-if="contactInfo.mobileNos && contactInfo.mobileNos.length > 1">
                    <template
                        v-for="(mobile, index) in contactInfo.mobileNos.slice(1)"
                        :key="`mob-${index + 1}-${String(contactInfo.mobileNos[index + 1] ?? '')}-${contactInfo.mobileNos.length}`"
                    >
                        <v-text-field
                            v-model="contactInfo.mobileNos[index + 1]"
                            :label="getMobileLabel(index + 1)"
                            :readonly="readonly"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            class="mb-2"
                        >
                            <template v-if="!readonly" v-slot:append>
                                <v-btn
                                    v-if="contactInfo.mobileNos.length > 1"
                                    icon="mdi-minus"
                                    size="small"
                                    variant="text"
                                    @click="removeMobile(index + 1)"
                                />
                                <v-btn
                                    v-if="index + 1 === contactInfo.mobileNos.length - 1"
                                    icon="mdi-plus"
                                    size="small"
                                    variant="text"
                                    @click="addMobile"
                                />
                            </template>
                        </v-text-field>
                    </template>
                </v-col>
                
                <v-col v-if="!addressSectionsOnly" cols="12">
                    <v-textarea
                        v-model="contactInfo.note"
                        :label="t('contact.Note') || 'Note'"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                        rows="3"
                    />
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>

    <ContactBranchAddressShowDialog
        v-model="showBranchAddressShowDialog"
        :items="branchShowDialogItems"
        @select="onBranchShowRowSelected"
    />

    <ContactAddressSelectDialog
        v-if="contactCdForSelect && !useBranchAddresses && isJurisdictionMY"
        v-model="showServiceSelectDialog"
        :contact-cd="contactCdForSelect"
        filter-address-type="M04002"
        @select="onServiceAddressCandidateSelected"
    />

    <ContactAddressSelectDialog
        v-if="contactCdForSelect && !useBranchAddresses && isJurisdictionMY"
        v-model="showBusinessSelectDialog"
        :contact-cd="contactCdForSelect"
        filter-address-type="M04003"
        @select="onBusinessAddressCandidateSelected"
    />
</template>

<style scoped lang="scss">
/* 폰트 패밀리 */
* {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.contact-info-section {
    background-color: rgb(var(--v-theme-surface)) !important;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
}

.my-address-summary {
    word-break: break-word;
}

.resync-action-btn {
    text-transform: none;
    font-weight: 600;
}

.contact-section-title {
    font-size: 18px;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    padding: 16px 24px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.contact-info-section :deep(.v-card-text) {
    padding: 24px !important;
}

.required-asterisk {
    color: #F44336 !important;
    font-weight: bold;
    margin-left: 2px;
}

/* Readonly 필드 스타일 (회색 배경) */
.readonly-field {
    opacity: 1 !important;
}

.readonly-field :deep(.v-field) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-input__control) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field__overlay) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(input),
.readonly-field :deep(textarea) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
    color: rgb(var(--v-theme-on-surface)) !important;
}

.readonly-field :deep(.v-field--variant-outlined) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
}

.readonly-field :deep(.v-field--variant-outlined .v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
}

.readonly-field.v-text-field--disabled,
.readonly-field.v-autocomplete--disabled,
.readonly-field.v-select--disabled {
    opacity: 1 !important;
}

.readonly-field.v-text-field--disabled :deep(.v-field),
.readonly-field.v-text-field--disabled :deep(.v-field__input),
.readonly-field.v-text-field--disabled :deep(.v-input__control),
.readonly-field.v-text-field--disabled :deep(.v-field__overlay),
.readonly-field.v-text-field--disabled :deep(input),
.readonly-field.v-text-field--disabled :deep(textarea),
.readonly-field.v-autocomplete--disabled :deep(.v-field),
.readonly-field.v-autocomplete--disabled :deep(.v-field__input),
.readonly-field.v-autocomplete--disabled :deep(.v-input__control),
.readonly-field.v-autocomplete--disabled :deep(.v-field__overlay),
.readonly-field.v-select--disabled :deep(.v-field),
.readonly-field.v-select--disabled :deep(.v-field__input),
.readonly-field.v-select--disabled :deep(.v-input__control),
.readonly-field.v-select--disabled :deep(.v-field__overlay) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
    color: rgb(var(--v-theme-on-surface)) !important;
}

/* Select readonly 스타일 */
.readonly-field.v-select {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-select :deep(.v-field) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-select :deep(.v-field__input) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-select :deep(.v-input__control) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-select :deep(.v-field__overlay) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

/* Autocomplete readonly일 때 값이 보이도록 */
.readonly-field.v-autocomplete :deep(.v-field__input) {
    opacity: 1 !important;
    visibility: visible !important;
}

.readonly-field.v-autocomplete :deep(.v-field__input input) {
    opacity: 1 !important;
    visibility: visible !important;
    color: rgb(var(--v-theme-on-surface)) !important;
    -webkit-text-fill-color: rgb(var(--v-theme-on-surface)) !important;
}

.readonly-field.v-autocomplete--disabled :deep(.v-field__input) {
    opacity: 1 !important;
    visibility: visible !important;
}

.readonly-field.v-autocomplete--disabled :deep(.v-field__input input) {
    opacity: 1 !important;
    visibility: visible !important;
    color: rgb(var(--v-theme-on-surface)) !important;
    -webkit-text-fill-color: rgb(var(--v-theme-on-surface)) !important;
}

/* Disabled 필드 스타일 (읽기 전용) - 항상 회색 배경 */
.postal-readonly-field {
    opacity: 1 !important;
}

.postal-readonly-field :deep(.v-field) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.postal-readonly-field :deep(.v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.postal-readonly-field :deep(.v-input__control) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.postal-readonly-field :deep(.v-field__overlay) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.postal-readonly-field :deep(input),
.postal-readonly-field :deep(textarea) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    cursor: not-allowed !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
    opacity: 1 !important;
}

.postal-readonly-field :deep(.v-field--variant-outlined) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.postal-readonly-field :deep(.v-field--variant-outlined .v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.postal-readonly-field.v-text-field--disabled {
    opacity: 1 !important;
}

.postal-readonly-field.v-text-field--disabled :deep(.v-field),
.postal-readonly-field.v-text-field--disabled :deep(.v-field__input),
.postal-readonly-field.v-text-field--disabled :deep(.v-input__control),
.postal-readonly-field.v-text-field--disabled :deep(.v-field__overlay),
.postal-readonly-field.v-text-field--disabled :deep(input),
.postal-readonly-field.v-text-field--disabled :deep(textarea) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
    opacity: 1 !important;
}

.register-address-card {
    background: rgb(var(--v-theme-surface)) !important;
    border-color: rgba(var(--v-border-color), var(--v-border-opacity)) !important;
}

.register-address-card-body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 18px 20px;
    flex-wrap: nowrap;
}

.register-address-card-main {
    flex: 1 1 auto;
    min-width: 0;
}

.register-address-card-title {
    font-size: 1rem;
    font-weight: 700;
    color: rgb(var(--v-theme-on-surface));
    line-height: 1.4;
    margin-bottom: 8px;
}

.register-address-card-text {
    font-size: 0.875rem;
    font-weight: 400;
    color: rgba(var(--v-theme-on-surface), 0.68);
    line-height: 1.5;
    word-break: break-word;
}

.register-address-action-btn {
    flex-shrink: 0;
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0;
    height: 36px !important;
    min-width: 84px;
    padding-inline: 16px !important;
}

.contact-info-section.register-business-address-host {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    margin-bottom: 0 !important;
}

.contact-info-section.register-business-address-host :deep(.v-card-text) {
    padding: 0 !important;
}
</style>

